import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { Project, NewProject, Task, NewTask, TaskComment, TaskCommentAttachment, TaskAttachment, ProjectAssignment, RecurrenceType, RecurrenceUnit, ProjectBriefDocument } from '../types';
import { triggerWhatsAppAutomation } from '../utils/whatsapp';
import { createActivityComment } from '../utils/task';
import { eventBus } from '../events/eventBus';
import { taskRealtimeService } from '../services/taskRealtimeService';
import { getUserData } from '../utils';

// Colonne di `projects` caricate dalla lista: tutte tranne `metadata`, che contiene
// status_history/task_history e arriva a decine di kB per riga. Della metadata la lista
// usa solo next_call_date e color, presi come chiavi singole (vedi PROJECT_METADATA_KEYS).
const PROJECT_LIST_COLUMNS = `
  id, name, description, objectives, client_id, status, start_date, due_date,
  completed_at, created_at, updated_at, organization_id, send_notifications,
  deal_id, project_type, archived, project_manager_id, urgency, estimated_delivery_date,
  next_call_date:metadata->>next_call_date,
  color:metadata->>color
`;

// Filtri per task paginate
export interface TaskFilters {
  assignedTo?: string | null;      // Filter by user
  status?: string[];               // ['todo', 'in_progress', 'review']
  priority?: string[];             // ['urgent', 'high', 'medium', 'low']
  showCompleted?: boolean;         // Include completed tasks
  searchQuery?: string;            // Search in title/description
}

// Stato paginazione task
export interface TaskPaginationState {
  cursor: string | null;           // created_at timestamp
  hasMore: boolean;
  loading: boolean;
}

const TASKS_PAGE_SIZE = 50;

// Fetch della lista progetti già in corso. Serve una guardia dedicata perché
// `loading` nello store è alzato anche dal dettaglio progetto.
let fetchProjectsInFlight = false;

export interface BulkCreateProgress {
  active: boolean;
  current: number;
  total: number;
  projectName: string;
  collapsed: boolean;
  result?: { success: boolean; message: string; created: number };
}

interface ProjectStore {
  projects: Project[];
  selectedProject: Project | null;
  tasks: Task[];
  loading: boolean;
  error: string | null;
  showArchived: boolean;
  bulkCreateProgress: BulkCreateProgress | null;
  setBulkCreateCollapsed: (collapsed: boolean) => void;
  dismissBulkCreateProgress: () => void;
  fetchProjects: (includeArchived?: boolean) => Promise<void>;
  fetchProjectById: (id: string) => Promise<void>;
  fetchProjectTasks: (projectId?: string) => Promise<void>;
  // Carica commenti e checklist completi di una singola task: la lista ne porta solo i conteggi
  fetchTaskDetails: (taskId: string) => Promise<void>;
  fetchAllProjectTasks: () => Promise<void>;
  createProject: (project: NewProject) => Promise<Project | null>;
  updateProject: (id: string, project: Partial<Project>, skipRefresh?: boolean) => Promise<Project | null>;
  deleteProject: (id: string) => Promise<void>;
  archiveProject: (id: string) => Promise<void>;
  unarchiveProject: (id: string) => Promise<void>;
  toggleShowArchived: () => void;
  createTask: (task: NewTask) => Promise<Task | null>;
  createTaskSection: (projectId: string, sectionName: string) => Promise<Task | null>;
  updateTask: (id: string, task: Partial<Task>, skipRefresh?: boolean) => Promise<Task | null>;
  deleteTask: (id: string) => Promise<void>;
  createTaskComment: (taskId: string, content: string) => Promise<TaskComment | null>;
  deleteTaskComment: (commentId: string, taskId: string) => Promise<void>;
  updateLocalTask: (id: string, task: Partial<Task>) => void;
  uploadTaskAttachment: (taskId: string, file: File) => Promise<TaskAttachment | null>;
  deleteTaskAttachment: (attachmentId: string) => Promise<void>;

  // Task checklist methods
  createTaskChecklistItem: (taskId: string, title: string, description?: string, sortOrder?: number) => Promise<any>;
  updateTaskChecklistItem: (checklistId: string, updates: any) => Promise<void>;
  deleteTaskChecklistItem: (checklistId: string) => Promise<void>;
  toggleChecklistItem: (checklistId: string, isCompleted: boolean) => Promise<void>;

  // Renewal task methods
  fetchRenewalTasks: () => Promise<Task[]>;
  completeRenewalTask: (taskId: string, dealServiceId: string) => Promise<void>;

  // Activity tracking
  createActivityComment: (taskId: string, activityType: string, details: any) => Promise<void>;

  // Project assignment methods
  assignUserToProject: (projectId: string, userId: string) => Promise<void>;
  unassignUserFromProject: (projectId: string, userId: string) => Promise<void>;
  fetchProjectAssignments: (projectId: string) => Promise<ProjectAssignment[]>;

  // Task JSON export/import methods
  exportProjectTasksAsJson: (projectId: string) => Promise<string>;
  importProjectTasksFromJson: (
    projectId: string,
    json: string,
    mode: 'replace' | 'append',
    onProgress?: (current: number, total: number) => void
  ) => Promise<{ success: boolean; imported: number; error?: string }>;
  createTasksBulk: (
    projectId: string,
    tasks: Array<{
      title: string;
      description?: string;
      status?: string;
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      assigned_to?: string;
      due_date?: string;
      section?: string;
      checklist?: Array<{ title: string; is_required: boolean }>;
      tags?: string[];
    }>,
    onProgress?: (current: number, total: number) => void
  ) => Promise<{ success: boolean; created: number; errors: string[] }>;
  updateTasksBulk: (
    projectId: string,
    updates: Array<{
      id: string;
      title?: string;
      description?: string;
      checklist?: Array<{ title: string; is_required: boolean }>;
    }>
  ) => Promise<{ success: boolean; updated: number; errors: string[] }>;

  // Quick inline update methods
  updateProjectQuick: (id: string, field: 'status' | 'project_manager_id' | 'urgency' | 'estimated_delivery_date', value: string | null) => Promise<boolean>;
  updateHealthStatusQuick: (projectId: string, healthStatus: 'on_track' | 'at_risk' | 'delayed') => Promise<boolean>;

  // Brief documents methods
  briefDocuments: ProjectBriefDocument[];
  fetchBriefDocuments: (projectId: string) => Promise<ProjectBriefDocument[]>;
  uploadBriefDocument: (projectId: string, file: File) => Promise<ProjectBriefDocument | null>;
  addBriefLink: (projectId: string, name: string, url: string) => Promise<ProjectBriefDocument | null>;
  renameBriefDocument: (documentId: string, newName: string) => Promise<void>;
  toggleBriefDocumentVisibility: (documentId: string, visible: boolean) => Promise<void>;
  deleteBriefDocument: (documentId: string) => Promise<void>;
  reorderBriefDocuments: (projectId: string, documentIds: string[]) => Promise<void>;

  // Client assignment methods
  projectClientAccesses: Array<{ id: string; client_id: string; username: string; client?: { business_name: string } }>;
  portalSharedSections: Set<string>;
  fetchProjectClientAccesses: (projectId: string) => Promise<void>;

  // Collaborator assignment methods
  projectCollaboratorAccesses: Array<{ id: string; external_partner_id: string; username: string; preferred_language: string; partner?: { id: string; name: string; company?: string } }>;
  fetchProjectCollaboratorAccesses: (projectId: string) => Promise<void>;
  toggleSectionPortalSharing: (projectId: string, sectionId: string) => Promise<void>;
  assignTaskToClient: (taskId: string, clientAccessId: string | null) => Promise<void>;
  bulkAssignTasksToClient: (taskIds: string[], clientAccessId: string | null) => Promise<void>;
  ensureUserAssignedToProject: (projectId: string, userId: string) => Promise<void>;

  // Client comments methods (for CRM to view/reply to client comments)
  taskClientComments: Array<{
    id: string;
    task_id: string;
    content: string;
    author_type: 'client' | 'staff';
    author_id: string;
    author_name?: string;
    is_read: boolean;
    created_at: string;
    attachments?: Array<{
      id: string;
      comment_id: string;
      name: string;
      url: string;
      type?: string;
      size?: number;
      uploader_type: string;
      uploader_id: string;
      created_at: string;
    }>;
  }>;
  taskClientCommentsLoading: boolean;
  fetchTaskClientComments: (taskId: string) => Promise<void>;
  replyToClientComment: (taskId: string, content: string, authorName: string, notifyClient?: boolean) => Promise<boolean>;
  markClientCommentsAsRead: (taskId: string) => Promise<void>;
  transcribeClientComment: (commentId: string, audioUrl: string) => Promise<string>;

  // Realtime local update methods (used by taskRealtimeService)
  updateTaskLocally: (taskId: string, updates: Partial<Task>) => void;
  addTaskLocally: (task: Task) => void;
  removeTaskLocally: (taskId: string) => void;
  addClientCommentLocally: (comment: { id: string; task_id: string; content: string; author_type: 'client' | 'staff'; author_id: string; author_name?: string; is_read: boolean; created_at: string; attachments?: Array<{ id: string; comment_id: string; name: string; url: string; type?: string; size?: number; uploader_type: string; uploader_id: string; created_at: string }> }) => void;

  // Comment attachment methods
  uploadCommentAttachment: (commentId: string, file: File) => Promise<TaskCommentAttachment | null>;
  deleteCommentAttachment: (attachmentId: string, commentId: string) => Promise<boolean>;
  createTaskCommentWithAttachments: (taskId: string, content: string, files: File[]) => Promise<TaskComment | null>;

  // Paginated tasks methods
  tasksPagination: TaskPaginationState;
  currentTaskFilters: TaskFilters | null;
  fetchTasksPaginated: (options: {
    filters?: TaskFilters;
    cursor?: string | null;
    limit?: number;
    reset?: boolean;
  }) => Promise<void>;
  fetchMoreTasks: () => Promise<void>;
}

// Funzioni utility per task ricorrenti
const calculateNextDueDate = (
  originalDueDate: string,
  recurrenceType: RecurrenceType,
  recurrenceInterval: number = 1,
  recurrenceUnit?: RecurrenceUnit
): string => {
  const date = new Date(originalDueDate);
  switch (recurrenceType) {
    case 'daily':
      date.setDate(date.getDate() + 1);
      break;
    case 'weekly':
      date.setDate(date.getDate() + 7);
      break;
    case 'biweekly':
      date.setDate(date.getDate() + 14);
      break;
    case 'monthly':
      date.setMonth(date.getMonth() + 1);
      break;
    case 'custom':
      if (recurrenceUnit === 'days') {
        date.setDate(date.getDate() + recurrenceInterval);
      } else if (recurrenceUnit === 'weeks') {
        date.setDate(date.getDate() + recurrenceInterval * 7);
      } else if (recurrenceUnit === 'months') {
        date.setMonth(date.getMonth() + recurrenceInterval);
      }
      break;
  }
  return date.toISOString();
};

const shouldCreateNextRecurrence = (task: Task): boolean => {
  if (!task.is_recurring || !task.due_date || !task.recurrence_type) return false;

  switch (task.recurrence_end_type) {
    case 'never':
      return true;
    case 'on_date':
      if (!task.recurrence_end_date) return true;
      // Calcola dalla due_date corrente, non dalla original_due_date
      const nextDue = calculateNextDueDate(
        task.due_date,
        task.recurrence_type,
        task.recurrence_interval,
        task.recurrence_unit
      );
      return new Date(nextDue) <= new Date(task.recurrence_end_date);
    case 'after_count':
      if (!task.recurrence_max_count) return true;
      return (task.recurrence_count || 0) < task.recurrence_max_count;
    default:
      return true; // Default: continua ricorrenza
  }
};

// ─── Helper: clona service_milestone_templates in project_milestones ────────
// Chiamato dopo createProject quando il progetto ha deal_id. Per ogni deal_service
// del deal, fetch dei milestone templates attivi del relativo service e clone
// snapshot in project_milestones. Idempotente: usa una select preliminare per
// evitare duplicati se il progetto ha gia milestone seedate da una run precedente.
// Plan: MD/plans/2026-05-30-service-milestones-plan.md
async function seedProjectMilestonesFromDeal(
  projectId: string,
  dealId: string,
  organizationId: string,
): Promise<void> {
  // Skip se il progetto ha gia milestone (idempotenza)
  const { data: existing } = await supabase
    .from('project_milestones')
    .select('id')
    .eq('project_id', projectId)
    .limit(1);
  if (existing && existing.length > 0) {
    console.log('[seedProjectMilestonesFromDeal] project already has milestones, skipping');
    return;
  }

  // Fetch deal_services del deal
  const { data: dealServices, error: dsErr } = await supabase
    .from('deal_services')
    .select('id, service_id')
    .eq('deal_id', dealId)
    .not('service_id', 'is', null);
  if (dsErr || !dealServices || dealServices.length === 0) return;

  const serviceIds = [...new Set(dealServices.map(d => d.service_id).filter(Boolean) as string[])];
  if (serviceIds.length === 0) return;

  // Fetch milestone templates per quei servizi
  const { data: templates, error: tplErr } = await supabase
    .from('service_milestone_templates')
    .select('id, service_id, name, description, sort_order, auto_complete_from_tasks, estimated_duration_days')
    .in('service_id', serviceIds)
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  if (tplErr || !templates || templates.length === 0) return;

  // Per ogni deal_service crea le sue milestone (un set per riga, perche'
  // 2 servizi uguali sullo stesso deal devono avere set indipendenti).
  const rows: any[] = [];
  let globalOrder = 0;
  for (const ds of dealServices) {
    const tplsForService = templates.filter(t => t.service_id === ds.service_id);
    for (const tpl of tplsForService) {
      rows.push({
        organization_id: organizationId,
        project_id: projectId,
        deal_service_id: ds.id,
        template_id: tpl.id,
        name: tpl.name,
        description: tpl.description ?? null,
        sort_order: globalOrder++,
        status: 'pending',
        completion_percent: 0,
        auto_complete_from_tasks: tpl.auto_complete_from_tasks ?? true,
      });
    }
  }
  if (rows.length === 0) return;

  const { error: insErr } = await supabase.from('project_milestones').insert(rows);
  if (insErr) {
    console.error('[seedProjectMilestonesFromDeal] insert error:', insErr.message);
    return;
  }
  console.log(`[seedProjectMilestonesFromDeal] seeded ${rows.length} milestones for project ${projectId}`);
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  projects: [],
  selectedProject: null,
  tasks: [],
  loading: false,
  error: null,
  showArchived: false,
  briefDocuments: [],
  projectClientAccesses: [],
  projectCollaboratorAccesses: [],
  portalSharedSections: new Set<string>(),
  taskClientComments: [],
  taskClientCommentsLoading: false,

  // Bulk create progress
  bulkCreateProgress: null,
  setBulkCreateCollapsed: (collapsed) => set(state => ({
    bulkCreateProgress: state.bulkCreateProgress
      ? { ...state.bulkCreateProgress, collapsed }
      : null
  })),
  dismissBulkCreateProgress: () => set({ bulkCreateProgress: null }),

  // Paginated tasks state
  tasksPagination: {
    cursor: null,
    hasMore: true,
    loading: false,
  },
  currentTaskFilters: null,

  fetchProjects: async (includeArchived = false) => {
    // Guardia contro fetch simultanee della LISTA. Non si guarda `state.loading`
    // perché quel flag lo alzano anche fetchProjectById/fetchProjectTasks: con il
    // drawer aperto un refresh della lista veniva saltato in silenzio.
    if (fetchProjectsInFlight) {
      console.log('fetchProjects: already loading, skipping');
      return;
    }
    fetchProjectsInFlight = true;

    set({ loading: true, error: null });
    try {
      // organization_id dalla cache condivisa (sessionStorage, 5 min) invece di
      // auth.getUser() + user_profiles ad ogni fetch
      const { organizationId } = await getUserData();

      if (!organizationId) {
        throw new Error('Organization ID not found for current user');
      }

      let query = supabase
        .from('projects')
        .select(`
          ${PROJECT_LIST_COLUMNS},
          client:clients!client_id (
            id,
            business_name
          ),
          project_manager:user_profiles!project_manager_id (
            id,
            email,
            full_name,
            avatar_url
          ),
          deal:deals!deal_id (
            id,
            title,
            signed_at,
            deal_services (
              id,
              service_id,
              amount,
              service:services!service_id (
                id,
                title,
                category_id,
                category:service_categories!category_id (
                  id,
                  name,
                  description,
                  color
                )
              )
            )
          ),
          assignments:project_assignments (
            id,
            user_id,
            assigned_at,
            assigned_by,
            user:user_profiles!project_assignments_user_id_fkey (
              id,
              email,
              full_name,
              avatar_url
            )
          )
        `)
        .eq('organization_id', organizationId);

      // Filter archived projects unless explicitly requested
      if (!includeArchived) {
        query = query.eq('archived', false);
      }

      query = query.order('created_at', { ascending: false });

      // I task NON completati bastano a tutto ciò che la lista calcola (task da fare,
      // prossima scadenza, ritardi, indicatori di salute): sono un terzo del totale.
      // Ciò che richiede anche i completati — progresso e "progetto senza task" — arriva
      // dalla vista degli aggregati. `projects!inner()` filtra per organizzazione senza
      // aggiungere campi alla risposta (tasks non ha organization_id).
      // PostgREST tronca a 1000 righe: i task non completati sono di più e senza
      // paginazione i conteggi degli indicatori risultavano più bassi del vero,
      // in modo silenzioso e plausibile. Si pagina finché la pagina è piena.
      const fetchIncompleteTasks = async () => {
        const PAGINA = 1000;
        const raccolti: any[] = [];
        for (let inizio = 0; ; inizio += PAGINA) {
          let q = supabase
            .from('tasks')
            .select('id, title, status, priority, due_date, assigned_to, is_header, is_client_request, project_id, project:projects!inner()')
            .eq('project.organization_id', organizationId)
            .or('status.is.null,status.neq.completed')
            .order('project_id', { ascending: true })
            .range(inizio, inizio + PAGINA - 1);

          if (!includeArchived) {
            q = q.eq('project.archived', false);
          }

          const { data, error: tasksError } = await q;
          if (tasksError) throw tasksError;
          raccolti.push(...(data || []));
          if (!data || data.length < PAGINA) return raccolti;
        }
      };

      // Aggregati e task sono contorni: se falliscono (es. la vista non è ancora
      // stata creata sull'ambiente) la lista deve comunque comparire, con meno
      // informazioni. Senza questa rete la pagina resta bianca.
      const senzaRompere = <T,>(p: Promise<T>, etichetta: string): Promise<T | null> =>
        p.catch(err => {
          console.error(`fetchProjects: ${etichetta} non disponibile`, err);
          return null;
        });

      // Le query sono indipendenti fra loro: in parallelo invece che in sequenza.
      // Status update, accessi portale, aggregati e task sono filtrati per organizzazione,
      // così non servono gli id dei progetti (niente centinaia di UUID in query string).
      const [
        { data: projects, error },
        { data: statusUpdates },
        { data: portalProjects },
        { data: orgUsers },
        aggregatiRes,
        incompleteTasks
      ] = await Promise.all([
        query,
        supabase
          .from('project_status_updates')
          .select('project_id, health_status, created_at, next_update_date')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false }),
        supabase
          .from('client_portal_projects')
          .select('project_id, client_access:client_portal_access!inner(is_active, organization_id)')
          .eq('client_access.organization_id', organizationId)
          .eq('client_access.is_active', true),
        // Gli assegnatari dei task vengono ricomposti qui: l'embed per-task su user_profiles
        // duplicava lo stesso profilo migliaia di volte nella risposta
        supabase
          .from('user_profiles')
          .select('id, email, full_name, avatar_url')
          .eq('organization_id', organizationId),
        senzaRompere(
          Promise.resolve(
            supabase
              .from('project_task_aggregates')
              .select('project_id, total_all, total_no_header, completed')
              .eq('organization_id', organizationId)
          ),
          'aggregati task'
        ),
        senzaRompere(fetchIncompleteTasks(), 'task non completati')
      ]);

      if (error) throw error;

      const taskAggregates = aggregatiRes?.data ?? null;

      if (projects && projects.length > 0) {
        // Create a map of project_id to latest status update
        const latestStatusMap = new Map<string, { health_status: string; created_at: string; next_update_date?: string }>();
        if (statusUpdates) {
          for (const update of statusUpdates) {
            if (!latestStatusMap.has(update.project_id)) {
              latestStatusMap.set(update.project_id, {
                health_status: update.health_status,
                created_at: update.created_at,
                next_update_date: update.next_update_date || undefined
              });
            }
          }
        }

        const portalAccessProjectIds = new Set<string>(
          (portalProjects || []).map((pp: any) => pp.project_id)
        );

        const usersById = new Map<string, any>(
          (orgUsers || []).map((u: any) => [u.id, u])
        );

        // Task non completati raggruppati per progetto, con l'assegnatario ricomposto
        const incompleteByProject = new Map<string, any[]>();
        for (const task of incompleteTasks || []) {
          const { project: _embedFiltroOrg, ...rest } = task as any;
          const lista = incompleteByProject.get(task.project_id) || [];
          lista.push({
            ...rest,
            assignee: task.assigned_to ? usersById.get(task.assigned_to) : undefined
          });
          incompleteByProject.set(task.project_id, lista);
        }

        const statsByProject = new Map<string, any>(
          (taskAggregates || []).map((a: any) => [a.project_id, a])
        );

        // Enrich projects with latest_status_update and portal access
        const enrichedProjects = projects.map((project: any) => {
          const { next_call_date, color, ...rest } = project;
          const stats = statsByProject.get(project.id);
          return {
            ...rest,
            // metadata ridotto alle sole chiavi usate dalla lista: vedi PROJECT_LIST_COLUMNS
            metadata: { next_call_date: next_call_date ?? undefined, color: color ?? undefined },
            // ATTENZIONE: qui ci sono SOLO i task non completati. Chi ha bisogno anche dei
            // completati (progresso, "progetto senza task") deve usare task_stats.
            tasks: incompleteByProject.get(project.id) || [],
            task_stats: {
              total_all: stats?.total_all ?? 0,
              total_no_header: stats?.total_no_header ?? 0,
              completed: stats?.completed ?? 0
            },
            latest_status_update: latestStatusMap.get(project.id) || null,
            has_client_portal_access: portalAccessProjectIds.has(project.id)
          };
        });

        set({ projects: enrichedProjects });
      } else {
        set({ projects: projects || [] });
      }
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      fetchProjectsInFlight = false;
      set({ loading: false });
    }
  },

  fetchProjectById: async (id) => {
    console.log('🔍 fetchProjectById called with ID:', id);
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          deal_id,
          project_manager_id,
          urgency,
          project_manager:user_profiles!project_manager_id (
            id,
            email,
            full_name,
            avatar_url
          ),
          assignments:project_assignments (
            id,
            user_id,
            assigned_at,
            assigned_by,
            user:user_profiles!project_assignments_user_id_fkey (
              id,
              email,
              full_name,
              avatar_url
            )
          )
        `)
        .eq('id', id)
        .eq('organization_id', profileData.organization_id);

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('Project not found');
      }

      if (data.length > 1) {
        console.warn('Multiple projects found with same ID:', id);
      }

      const project = data[0];

      // Load client data separately if client_id exists
      let projectWithClient = project;
      if (project?.client_id) {
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('id, business_name, email, phone')
          .eq('id', project.client_id)
          .single();

        if (!clientError && clientData) {
          projectWithClient = { ...project, client: clientData };
        }
      }

      // Get latest status update for this project
      const { data: statusUpdate } = await supabase
        .from('project_status_updates')
        .select('health_status, created_at')
        .eq('project_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (statusUpdate) {
        projectWithClient = {
          ...projectWithClient,
          latest_status_update: statusUpdate
        };
      }

      set({ selectedProject: projectWithClient });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  fetchProjectTasks: async (projectId) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Di commenti e checklist qui servono solo i conteggi mostrati sulla riga della
      // task: il contenuto lo carica fetchTaskDetails all'apertura del drawer. Con
      // l'embed completo un progetto da 3.600 commenti sfondava il timeout di 8s.
      let query = supabase
        .from('tasks')
        .select(`
          id,
          project_id,
          title,
          description,
          status,
          priority,
          assigned_to,
          assigned_client_access_id,
          assigned_collaborator_access_id,
          due_date,
          start_date,
          completed_at,
          created_at,
          updated_at,
          created_by,
          section,
          "order",
          is_header,
          is_client_request,
          sprint_added_at,
          review_at,
          dependencies,
          is_recurring,
          recurrence_type,
          recurrence_interval,
          recurrence_unit,
          recurrence_end_type,
          recurrence_end_date,
          recurrence_max_count,
          recurrence_count,
          recurrence_parent_id,
          recurrence_source_id,
          original_due_date,
          reminder_days_before,
          location,
          custom_fields,
          metadata,
          time_entries:task_time_entries (duration_minutes, ended_at),
          project:projects!inner (
            id,
            name,
            metadata,
            organization_id,
            client:clients (
              id,
              business_name
            )
          ),
          assignee:user_profiles!tasks_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          created_by_user:user_profiles!tasks_created_by_fkey (
            id,
            email,
            full_name
          ),
          comments:task_comments (
            id,
            is_system_comment
          ),
          attachments:task_attachments (
            id,
            name,
            url,
            type,
            size,
            created_at,
            created_by
          ),
          checklist:task_checklist!left (
            id,
            is_completed
          ),
          tags:task_tag_assignments!left (
            tag:project_task_tags!inner (
              id,
              name,
              color,
              sort_order,
              is_active
            )
          )
        `)
        .eq('project.organization_id', userProfile.organization_id)
        .eq('project.archived', false)
        .order('created_at', { ascending: true });

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { data: tasks, error: tasksError } = await query;
      if (tasksError) throw tasksError;

      // Transform tasks to extract tags from assignments
      const transformedTasks = (tasks || []).map((task: any) => ({
        ...task,
        tags: task.tags
          ?.map((assignment: any) => assignment.tag)
          .filter((tag: any) => tag && tag.is_active)
          .sort((a: any, b: any) => a.sort_order - b.sort_order) || [],
        // Somma delle sole voci chiuse: un timer in corso non deve comparire nel totale di riga
        total_time_minutes: (task.time_entries || [])
          .filter((e: any) => e.ended_at !== null)
          .reduce((sum: number, e: any) => sum + (e.duration_minutes ?? 0), 0)
      }));

      set({ tasks: transformedTasks });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  // La lista porta i soli conteggi di commenti e checklist. Qui si scarica il contenuto
  // vero della singola task, quando serve davvero: all'apertura del suo drawer.
  // Non tocca `loading`, che è condiviso con la lista e la farebbe tornare scheletro.
  fetchTaskDetails: async (taskId) => {
    try {
      const [commentiRes, checklistRes] = await Promise.all([
        supabase
          .from('task_comments')
          .select(`
            id,
            content,
            created_at,
            is_system_comment,
            author:user_profiles!task_comments_created_by_fkey (
              id,
              email,
              full_name
            ),
            attachments:task_comment_attachments (
              id,
              comment_id,
              name,
              url,
              type,
              size,
              created_by,
              created_at
            )
          `)
          .eq('task_id', taskId)
          .order('created_at', { ascending: true }),
        supabase
          .from('task_checklist')
          .select(`
            id,
            title,
            description,
            is_required,
            is_completed,
            completed_at,
            estimated_minutes,
            time_spent_minutes,
            sort_order,
            completed_by
          `)
          .eq('task_id', taskId)
          .order('sort_order', { ascending: true })
      ]);

      if (commentiRes.error) throw commentiRes.error;
      if (checklistRes.error) throw checklistRes.error;

      set(state => ({
        tasks: state.tasks.map(t =>
          t.id === taskId
            ? { ...t, comments: (commentiRes.data as any) || [], checklist: (checklistRes.data as any) || [] }
            : t
        )
      }));
    } catch (error) {
      console.error('fetchTaskDetails: dettagli della task non caricati', error);
    }
  },

  fetchAllProjectTasks: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user and organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;

      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select(`
          id,
          project_id,
          title,
          description,
          status,
          priority,
          assigned_to,
          due_date,
          start_date,
          completed_at,
          created_at,
          updated_at,
          section,
          "order",
          is_header,
          is_client_request,
          estimated_hours,
          actual_hours,
          time_entries:task_time_entries (duration_minutes, ended_at),
          dependencies,
          is_recurring,
          recurrence_type,
          recurrence_interval,
          recurrence_unit,
          recurrence_end_type,
          recurrence_end_date,
          recurrence_max_count,
          recurrence_count,
          recurrence_parent_id,
          recurrence_source_id,
          original_due_date,
          reminder_days_before,
          location,
          custom_fields,
          metadata,
          project:projects!inner (
            id,
            name,
            status,
            client_id,
            organization_id,
            client:clients (
              id,
              business_name
            )
          ),
          assignee:user_profiles!tasks_assigned_to_fkey (
            id,
            email,
            full_name
          )
        `)
        .eq('is_header', false)
        .eq('project.organization_id', profileData.organization_id)
        .order('created_at', { ascending: true });

      if (tasksError) throw tasksError;

      // DEBUG: Log quante task sono state caricate
      console.log('[Gantt Debug] Tasks loaded from DB:', tasks?.length || 0);
      console.log('[Gantt Debug] Sample tasks:', tasks?.slice(0, 3));
      console.log('[Gantt Debug] Tasks by project:', tasks?.reduce((acc: any, t) => {
        const projectName = t.project?.name || 'Unknown';
        acc[projectName] = (acc[projectName] || 0) + 1;
        return acc;
      }, {}));

      set({ tasks: tasks || [], loading: false });
    } catch (error) {
      console.error('Error fetching all project tasks:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  createProject: async (project) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;

      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('projects')
        .insert({
          ...project,
          organization_id: profileData.organization_id
        })
        .select()
        .single();

      if (error) throw error;

      // Auto-seed project_milestones dai template del servizio del deal di origine
      // (Plan C: ogni deal_service.service_id ha 0..N service_milestone_templates;
      // qui le cloniamo come istanze di progetto. Soft-fail per non bloccare la
      // creazione del progetto).
      if (data?.id && data?.deal_id) {
        seedProjectMilestonesFromDeal(data.id, data.deal_id, profileData.organization_id)
          .catch(err => console.warn('[ProjectStore] milestone seed failed (non-blocking):', err));
      }

      // Trigger WhatsApp automation for project creation
      triggerWhatsAppAutomation({
        entityType: 'project',
        triggerEvent: 'create',
        entityData: data
      }).catch(err => console.error('Error triggering WhatsApp automation:', err));

      // Emit workflow trigger event
      try {
        eventBus.emitEvent('project:created' as any, {
          projectId: data.id,
          projectName: data.name,
          clientId: data.client_id,
          status: data.status,
          projectManagerId: data.project_manager_id,
          organizationId: profileData.organization_id,
        });
      } catch (e) { console.warn('[ProjectStore] Failed to emit project:created event:', e); }

      const projects = get().projects;
      set({ projects: [data, ...projects] });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateProject: async (id, project, skipRefresh = false) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // Get current project data to detect status changes
      const { data: current } = await supabase
        .from('projects')
        .select('status, metadata')
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .single();

      const oldStatus = current?.status;
      const isStatusChange = project.status && project.status !== oldStatus;

      // La lista progetti tiene in memoria un metadata ridotto (solo next_call_date/color):
      // senza merge, un salvataggio partito da lì cancellerebbe status_history e task_history
      const payload = project.metadata
        ? { ...project, metadata: { ...(current?.metadata || {}), ...project.metadata } }
        : project;

      const { data, error } = await supabase
        .from('projects')
        .update(payload)
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .select()
        .single();

      if (error) throw error;
      
      // Trigger appropriate WhatsApp automation
      if (isStatusChange) {
        // Status change specific event
        triggerWhatsAppAutomation({
          entityType: 'project',
          triggerEvent: 'status_change',
          entityData: data,
          oldStatus
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));

        // Emit notification event for project status change
        eventBus.emitEvent({
          type: 'project:status_changed',
          payload: {
            projectId: id,
            fromStatus: oldStatus,
            toStatus: data.status,
            projectName: data.name,
            clientId: data.client_id
          }
        });
      } else {
        // Generic update event
        triggerWhatsAppAutomation({
          entityType: 'project',
          triggerEvent: 'update',
          entityData: data
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      }

      // Update both projects and selectedProject
      const projects = get().projects.map(p => 
        p.id === id ? { ...p, ...data } : p
      );
      
      if (!skipRefresh) {
        set({ 
          projects,
          selectedProject: data
        });
      }
      
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteProject: async (id) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;

      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // Get the project before deleting for the trigger
      const { data: projectData } = await supabase
        .from('projects')
        .select(`
          *,
          client:clients (
            id,
            business_name
          )
        `)
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .single();

      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id)
        .eq('organization_id', profileData.organization_id);

      if (error) throw error;

      // Trigger WhatsApp automation for deletion
      if (projectData) {
        triggerWhatsAppAutomation({
          entityType: 'project',
          triggerEvent: 'delete',
          entityData: projectData
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      }

      const projects = get().projects.filter(p => p.id !== id);
      set({ projects });
    } catch (error) {
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  archiveProject: async (id) => {
    try {
      const result = await get().updateProject(id, { archived: true }, true);
      if (result) {
        // Remove from projects list if not showing archived
        const showArchived = get().showArchived;
        if (!showArchived) {
          const projects = get().projects.filter(p => p.id !== id);
          set({ projects });
        }
      }
    } catch (error) {
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  unarchiveProject: async (id) => {
    try {
      const result = await get().updateProject(id, { archived: false }, true);
      if (result) {
        // Refresh projects list
        await get().fetchProjects(get().showArchived);
      }
    } catch (error) {
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  toggleShowArchived: () => {
    const newShowArchived = !get().showArchived;
    set({ showArchived: newShowArchived });
    // Refresh projects with the new filter
    get().fetchProjects(newShowArchived);
  },

  createTask: async (task) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('tasks')
        .insert({
          ...task,
          created_by: userData.user.id
        })
        .select(`
          id,
          project_id,
          title,
          description,
          status,
          priority,
          assigned_to,
          due_date,
          start_date,
          completed_at,
          created_at,
          updated_at,
          section,
          "order",
          is_header,
          is_client_request,
          sprint_added_at,
          review_at,
          dependencies,
          is_recurring,
          recurrence_type,
          recurrence_interval,
          recurrence_unit,
          recurrence_end_type,
          recurrence_end_date,
          recurrence_max_count,
          recurrence_count,
          recurrence_parent_id,
          recurrence_source_id,
          original_due_date,
          reminder_days_before,
          location,
          custom_fields,
          metadata,
          assignee:user_profiles!tasks_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          comments:task_comments (
            id,
            content,
            created_at,
            is_system_comment,
            author:user_profiles!task_comments_created_by_fkey (
              id,
              email,
              full_name
            ),
            attachments:task_comment_attachments (
              id,
              comment_id,
              name,
              url,
              type,
              size,
              created_by,
              created_at
            )
          ),
          checklist:task_checklist!left (
            id,
            title,
            description,
            is_required,
            is_completed,
            completed_at,
            estimated_minutes,
            time_spent_minutes,
            sort_order,
            completed_by
          )
        `)
        .single();

      if (error) throw error;

      // Track task creation activity
      const { createActivityComment } = get();
      createActivityComment(data.id, 'task_created', {});

      // Emetti evento se la task è stata assegnata durante la creazione
      if (data.assigned_to && data.assigned_to !== userData.user.id) {
        const projectName = get().selectedProject?.name || '';
        eventBus.emitEvent({
          type: 'task:assigned',
          payload: {
            projectId: data.project_id || '',
            taskId: data.id,
            taskTitle: data.title,
            assignedToUserId: data.assigned_to,
            assignedByUserId: userData.user.id,
            projectName,
          }
        });
      }

      // Emit workflow trigger event for task creation
      try {
        const project = get().selectedProject;
        eventBus.emitEvent('task:created' as any, {
          taskId: data.id,
          taskTitle: data.title,
          taskStatus: data.status,
          projectId: data.project_id,
          projectName: project?.name || '',
          clientId: project?.client_id || '',
          assignedTo: data.assigned_to,
          priority: data.priority,
        });
      } catch (e) { console.warn('[ProjectStore] Failed to emit task:created event:', e); }

      // Auto-assign user to project if not already a member (fire-and-forget)
      if (data.assigned_to && data.project_id) {
        get().ensureUserAssignedToProject(data.project_id, data.assigned_to);
      }

      // Update tasks list
      const tasks = get().tasks;
      set({ tasks: [...tasks, data] });

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  createTaskSection: async (projectId: string, sectionName: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Generate a unique section ID based on sanitized section name
      const sectionId = `section_${sectionName.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;

      // Get max order value for this project to append at the end
      const { data: maxOrderData } = await supabase
        .from('tasks')
        .select('order')
        .eq('project_id', projectId)
        .order('order', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextOrder = maxOrderData?.order ? maxOrderData.order + 1 : 1;

      // Create a task header
      const headerTask: NewTask = {
        project_id: projectId,
        title: sectionName,
        status: 'todo', // Headers don't have a meaningful status
        priority: 'medium', // Default priority (required by database)
        is_header: true,
        section: sectionId,
        order: nextOrder, // Sequential ordering
        created_by: userData.user.id
      };

      const { data, error } = await supabase
        .from('tasks')
        .insert({
          ...headerTask,
          created_by: userData.user.id
        })
        .select(`
          id,
          project_id,
          title,
          description,
          status,
          priority,
          assigned_to,
          due_date,
          completed_at,
          created_at,
          updated_at,
          section,
          "order",
          is_header,
          is_client_request,
          sprint_added_at,
          review_at,
          dependencies
        `)
        .single();

      if (error) throw error;

      // Update tasks list
      const tasks = get().tasks;
      set({ tasks: [...tasks, data] });

      return data;
    } catch (error) {
      console.error('[ProjectStore] createTaskSection failed:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateLocalTask: (id: string, task: Partial<Task>) => {
    const tasks = get().tasks.map(t => 
      t.id === id ? { ...t, ...task } : t
    );
    set({ tasks });
  },

  updateTask: async (id: string, task: Partial<Task>, skipRefresh = false) => {
    try {
      let originalTask = get().tasks.find(t => t.id === id);
      if (!originalTask) {
        // Fallback: cerca la task embedded nei progetti (usata dalla dashboard)
        for (const project of get().projects) {
          const found = project.tasks?.find(t => t.id === id);
          if (found) {
            originalTask = found;
            break;
          }
        }
      }
      if (!originalTask) throw new Error('Task not found');

      // Stringa vuota su colonne timestamptz = errore 22007: una data svuotata va salvata come null
      for (const key of ['due_date', 'start_date', 'review_at', 'recurrence_end_date', 'original_due_date'] as const) {
        if ((task as Record<string, unknown>)[key] === '') (task as Record<string, unknown>)[key] = null;
      }

      const { createActivityComment } = get();

      // Handle status changes
      if (task.status === 'completed' && originalTask.status !== 'completed') {
        task.completed_at = new Date().toISOString();
        // Don't automatically move task to 'completed' section - keep it in current section

        // Track completion activity
        createActivityComment(id, 'task_completed', {});

        // Crea prossima task ricorrente se necessario
        if (originalTask.is_recurring && shouldCreateNextRecurrence(originalTask)) {
          // Usa la due_date corrente della task completata per calcolare la prossima
          const nextDueDate = calculateNextDueDate(
            originalTask.due_date!,
            originalTask.recurrence_type!,
            originalTask.recurrence_interval,
            originalTask.recurrence_unit
          );

          // Calcola start_date mantenendo la differenza con due_date
          let nextStartDate: string | undefined;
          if (originalTask.start_date && originalTask.due_date) {
            const startDiff = new Date(originalTask.due_date).getTime() - new Date(originalTask.start_date).getTime();
            nextStartDate = new Date(new Date(nextDueDate).getTime() - startDiff).toISOString();
          }

          // DEBUG: Log per verificare assigned_to
          console.log('[RECURRENCE DEBUG] Creating new recurring task');
          console.log('[RECURRENCE DEBUG] Original task assigned_to:', originalTask.assigned_to);
          console.log('[RECURRENCE DEBUG] Original task assignee:', originalTask.assignee);

          const newRecurringTask: NewTask = {
            project_id: originalTask.project_id,
            title: originalTask.title,
            description: originalTask.description,
            status: 'todo',
            priority: originalTask.priority,
            // FIX: Preserva assigned_to esplicitamente
            ...(originalTask.assigned_to && { assigned_to: originalTask.assigned_to }),
            due_date: nextDueDate,
            start_date: nextStartDate,
            section: originalTask.section,
            is_recurring: true,
            recurrence_type: originalTask.recurrence_type,
            recurrence_interval: originalTask.recurrence_interval,
            recurrence_unit: originalTask.recurrence_unit,
            recurrence_end_type: originalTask.recurrence_end_type,
            recurrence_end_date: originalTask.recurrence_end_date,
            recurrence_max_count: originalTask.recurrence_max_count,
            recurrence_parent_id: originalTask.recurrence_parent_id || originalTask.id,
            recurrence_source_id: originalTask.id,
            original_due_date: originalTask.original_due_date || originalTask.due_date,
            recurrence_count: (originalTask.recurrence_count || 0) + 1,
          };

          // Verifica che assigned_to sia preservato
          if (originalTask.assigned_to && !newRecurringTask.assigned_to) {
            console.error('[RECURRENCE ERROR] assigned_to was lost during recurring task creation!');
            console.error('[RECURRENCE ERROR] Original:', originalTask.assigned_to);
          }

          // Crea la nuova task ricorrente
          await get().createTask(newRecurringTask);

          // Log activity per ricorrenza creata con info assegnazione
          createActivityComment(id, 'recurrence_created', {
            nextDueDate: new Date(nextDueDate).toLocaleDateString('it-IT'),
            assignedTo: originalTask.assignee?.full_name || 'Non assegnata',
            recurrenceCount: (originalTask.recurrence_count || 0) + 1
          });
        }
      } else if (task.status !== 'completed' && originalTask.status === 'completed') {
        task.completed_at = null;
        // Don't change section when uncompleting - keep current section

        // Track reopening activity
        createActivityComment(id, 'task_reopened', {});
      } else if (task.status && task.status !== originalTask.status) {
        // Track other status changes
        createActivityComment(id, 'status_change', {
          oldStatus: originalTask.status,
          newStatus: task.status
        });
      }

      // Track priority changes
      if (task.priority && task.priority !== originalTask.priority) {
        createActivityComment(id, 'priority_change', {
          oldPriority: originalTask.priority,
          newPriority: task.priority
        });
      }

      // Track assignee changes
      if (task.assigned_to !== originalTask.assigned_to) {
        const oldAssignee = originalTask.assignee?.full_name || originalTask.assignee?.email;
        const newAssigneeUser = task.assigned_to ? 
          get().tasks.find(t => t.assignee?.id === task.assigned_to)?.assignee?.full_name ||
          get().tasks.find(t => t.assignee?.id === task.assigned_to)?.assignee?.email : null;
        
        createActivityComment(id, 'assignee_change', {
          oldAssignee,
          newAssignee: newAssigneeUser
        });
      }

      // Track due date changes
      if (task.due_date !== originalTask.due_date) {
        createActivityComment(id, 'due_date_change', {
          oldDueDate: originalTask.due_date,
          newDueDate: task.due_date
        });
      }

      // Track title changes
      if (task.title && task.title !== originalTask.title) {
        createActivityComment(id, 'title_change', {
          oldTitle: originalTask.title,
          newTitle: task.title
        });
      }

      // Track description changes (only for significant changes)
      if (task.description !== undefined && task.description !== originalTask.description) {
        createActivityComment(id, 'description_change', {});
      }

      // Handle section changes
      if (task.section === 'sprint' && originalTask.section !== 'sprint') {
        task.sprint_added_at = new Date().toISOString();
        if (task.status === 'completed') {
          task.status = 'todo';
          task.completed_at = null;
        }
      }

      // Track section changes
      if (task.section && task.section !== originalTask.section) {
        createActivityComment(id, 'section_change', {
          oldSection: originalTask.section,
          newSection: task.section
        });
      }

      const { data, error } = await supabase
        .from('tasks')
        .update(task)
        .eq('id', id)
        .select(`
          id,
          project_id,
          title,
          description,
          status,
          priority,
          assigned_to,
          assigned_client_access_id,
          assigned_collaborator_access_id,
          due_date,
          start_date,
          completed_at,
          created_at,
          updated_at,
          section,
          "order",
          sprint_added_at,
          review_at,
          dependencies,
          is_recurring,
          recurrence_type,
          recurrence_interval,
          recurrence_unit,
          recurrence_end_type,
          recurrence_end_date,
          recurrence_max_count,
          recurrence_count,
          recurrence_parent_id,
          recurrence_source_id,
          original_due_date,
          reminder_days_before,
          location,
          custom_fields,
          metadata,
          project:projects!inner (
            id,
            name,
            metadata,
            client:clients (
              id,
              business_name
            )
          ),
          assignee:user_profiles!tasks_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          comments:task_comments (
            id,
            content,
            created_at,
            is_system_comment,
            author:user_profiles!task_comments_created_by_fkey (
              id,
              email,
              full_name
            ),
            attachments:task_comment_attachments (
              id,
              comment_id,
              name,
              url,
              type,
              size,
              created_by,
              created_at
            )
          ),
          checklist:task_checklist!left (
            id,
            title,
            description,
            is_required,
            is_completed,
            completed_at,
            estimated_minutes,
            time_spent_minutes,
            sort_order,
            completed_by
          )
        `)
        .single();

      if (error) throw error;

      // Emetti eventi per le notifiche
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        const projectName = (data.project as any)?.name || get().selectedProject?.name || '';

        // Evento per cambio assegnazione
        if (task.assigned_to !== undefined && task.assigned_to !== originalTask.assigned_to) {
          if (task.assigned_to && task.assigned_to !== userData.user.id) {
            eventBus.emitEvent({
              type: 'task:assigned',
              payload: {
                projectId: originalTask.project_id || '',
                taskId: originalTask.id,
                taskTitle: data.title || originalTask.title,
                assignedToUserId: task.assigned_to,
                assignedByUserId: userData.user.id,
                projectName,
              }
            });
          }

          // Auto-assign user to project if not already a member (fire-and-forget)
          if (task.assigned_to && originalTask.project_id) {
            get().ensureUserAssignedToProject(originalTask.project_id, task.assigned_to);
          }
        }

        // Evento per cambio stato
        if (task.status && task.status !== originalTask.status) {
          eventBus.emitEvent({
            type: 'task:status_changed',
            payload: {
              projectId: originalTask.project_id || '',
              taskId: originalTask.id,
              taskTitle: originalTask.title,
              taskSection: data.section || originalTask.section,
              fromStatus: originalTask.status,
              toStatus: task.status,
              changedByUserId: userData.user.id,
              assignedToUserId: originalTask.assigned_to || undefined,
              createdByUserId: originalTask.created_by || undefined,
              projectName,
            }
          });
        }
      }

      if (!skipRefresh) {
        const tasks = get().tasks.map(t =>
          t.id === id ? { ...t, ...data } : t
        );
        set({ tasks });
      }

      // Aggiorna anche la task embedded nei progetti (per la dashboard)
      const updatedProjects = get().projects.map(p => {
        if (!p.tasks?.some(t => t.id === id)) return p;
        return {
          ...p,
          tasks: p.tasks.map(t => t.id === id ? { ...t, ...data } : t)
        };
      });
      set({ projects: updatedProjects });

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteTask: async (id) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Delete related records explicitly to avoid RLS conflicts with CASCADE
      // 1. Delete task comments
      const { error: commentsError } = await supabase
        .from('task_comments')
        .delete()
        .eq('task_id', id);

      if (commentsError) throw commentsError;

      // 2. Delete task attachments
      const { error: attachmentsError } = await supabase
        .from('task_attachments')
        .delete()
        .eq('task_id', id);

      if (attachmentsError) throw attachmentsError;

      // 3. Delete task checklist items
      const { error: checklistError } = await supabase
        .from('task_checklist')
        .delete()
        .eq('task_id', id);

      if (checklistError) throw checklistError;

      // 4. Finally delete the task
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update tasks list
      const tasks = get().tasks.filter(t => t.id !== id);
      set({ tasks });
    } catch (error) {
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  createTaskComment: async (taskId: string, content: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Get task info with project for notification (include task assignee/creator for configurable recipients)
      const { data: taskInfo } = await supabase
        .from('tasks')
        .select('project_id, title, assigned_to, created_by, project:projects(name)')
        .eq('id', taskId)
        .single();

      const { data, error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          content,
          created_by: userData.user.id,
          is_public: true
        })
        .select(`
          id,
          content,
          created_at,
          author:user_profiles!task_comments_created_by_fkey (
            id,
            email,
            full_name
          )
        `)
        .single();

      if (error) throw error;

      // Emit notification event for project comment
      if (taskInfo?.project_id && taskInfo?.project) {
        eventBus.emitEvent({
          type: 'project:comment',
          payload: {
            projectId: taskInfo.project_id,
            taskId,
            taskTitle: taskInfo.title || '',
            taskAssigneeId: taskInfo.assigned_to || undefined,
            taskCreatorId: taskInfo.created_by || undefined,
            commentId: data.id,
            commentContent: content,
            projectName: (taskInfo.project as { name: string }).name
          }
        });
      }

      // Aggiunge il commento sia all'elenco piatto delle task sia a quelle
      // annidate nei progetti: la dashboard progetti legge da li' e senza
      // questo secondo passaggio il commento appena scritto non compariva.
      const conCommento = (task: Task) => ({
        ...task,
        comments: [...(task.comments || []), data]
      });

      set(state => ({
        tasks: state.tasks.map(t => (t.id === taskId ? conCommento(t) : t)),
        projects: state.projects.map(p =>
          p.tasks?.some(t => t.id === taskId)
            ? { ...p, tasks: p.tasks.map(t => (t.id === taskId ? conCommento(t) : t)) }
            : p
        )
      }));

      // Broadcast commento per sync realtime con portale collaboratore
      taskRealtimeService.broadcastComment(taskId, data, 'crm');

      return data;
    } catch (error) {
      console.error('Error creating task comment:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteTaskComment: async (commentId: string, taskId: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Verifica che il commento appartenga all'utente corrente e recupera il contenuto
      const { data: comment, error: getError } = await supabase
        .from('task_comments')
        .select('created_by, content')
        .eq('id', commentId)
        .single();

      if (getError) throw getError;

      if (comment?.created_by !== userData.user.id) {
        throw new Error('Non puoi eliminare commenti di altri utenti');
      }

      // Salva il contenuto prima di eliminare
      const deletedContent = comment.content;

      const { error } = await supabase
        .from('task_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      // Crea evento di sistema per tracciare l'eliminazione
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('full_name, email')
        .eq('id', userData.user.id)
        .single();

      const userName = userProfile?.full_name || userProfile?.email || 'Un utente';
      const now = new Date().toLocaleString('it-IT');

      const systemContent = `**${userName}** ha eliminato un commento il ${now}\n\n**Contenuto eliminato:**\n> ${deletedContent}`;

      await supabase.from('task_comments').insert({
        task_id: taskId,
        content: systemContent,
        created_by: userData.user.id,
        organization_id: userData.user.user_metadata?.organization_id,
        is_system_comment: true
      });

      // Update local state and reload comments to include system event
      const { data: allComments } = await supabase
        .from('task_comments')
        .select(`
          id, content, created_at, is_system_comment,
          author:user_profiles!task_comments_created_by_fkey (id, email, full_name)
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: true });

      const tasks = get().tasks.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            comments: allComments || []
          };
        }
        return task;
      });

      set({ tasks });
    } catch (error) {
      console.error('Error deleting task comment:', error);
      throw error;
    }
  },

  updateTaskComment: async (commentId: string, taskId: string, newContent: string, oldContent: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Verifica che il commento appartenga all'utente corrente
      const { data: comment, error: getError } = await supabase
        .from('task_comments')
        .select('created_by')
        .eq('id', commentId)
        .single();

      if (getError) throw getError;

      if (comment?.created_by !== userData.user.id) {
        throw new Error('Non puoi modificare commenti di altri utenti');
      }

      // Aggiorna il commento
      const { data, error } = await supabase
        .from('task_comments')
        .update({ content: newContent })
        .eq('id', commentId)
        .select(`
          id, content, created_at,
          author:user_profiles!task_comments_created_by_fkey (id, email, full_name)
        `)
        .single();

      if (error) throw error;

      // Update local state immediatamente (no reload, no system comment)
      const tasks = get().tasks.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            comments: (task.comments || []).map(c =>
              c.id === commentId ? data : c
            )
          };
        }
        return task;
      });

      set({ tasks });
    } catch (error) {
      console.error('Error updating task comment:', error);
      throw error;
    }
  },

  // Comment Attachment Methods
  uploadCommentAttachment: async (commentId: string, file: File) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData) throw new Error('User profile not found');

      // Upload file to storage
      const fileName = `${Date.now()}-${file.name}`;
      const filePath = `task-comments/${commentId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      if (!urlData.publicUrl) throw new Error('Failed to get public URL');

      // Create attachment record
      const { data: attachment, error: dbError } = await supabase
        .from('task_comment_attachments')
        .insert({
          comment_id: commentId,
          name: file.name,
          url: urlData.publicUrl,
          type: file.type,
          size: file.size,
          created_by: userData.user.id,
          organization_id: profileData.organization_id
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Update local state - add attachment to the comment
      const tasks = get().tasks.map(task => ({
        ...task,
        comments: (task.comments || []).map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              attachments: [...(comment.attachments || []), attachment]
            };
          }
          return comment;
        })
      }));

      set({ tasks });
      return attachment as TaskCommentAttachment;
    } catch (error) {
      console.error('Error uploading comment attachment:', error);
      return null;
    }
  },

  deleteCommentAttachment: async (attachmentId: string, commentId: string) => {
    try {
      // Get attachment info
      const { data: attachment, error: getError } = await supabase
        .from('task_comment_attachments')
        .select('url')
        .eq('id', attachmentId)
        .single();

      if (getError) throw getError;

      // Extract file path and delete from storage
      try {
        const url = new URL(attachment.url);
        const pathParts = url.pathname.split('/');
        // Format: /storage/v1/object/public/documents/task-comments/{commentId}/{filename}
        const bucketIndex = pathParts.findIndex(p => p === 'documents');
        if (bucketIndex !== -1) {
          const filePath = pathParts.slice(bucketIndex + 1).join('/');
          await supabase.storage.from('documents').remove([filePath]);
        }
      } catch (storageError) {
        console.warn('Error deleting file from storage:', storageError);
        // Continue with database deletion even if storage deletion fails
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('task_comment_attachments')
        .delete()
        .eq('id', attachmentId);

      if (dbError) throw dbError;

      // Update local state
      const tasks = get().tasks.map(task => ({
        ...task,
        comments: (task.comments || []).map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              attachments: (comment.attachments || []).filter(a => a.id !== attachmentId)
            };
          }
          return comment;
        })
      }));

      set({ tasks });
      return true;
    } catch (error) {
      console.error('Error deleting comment attachment:', error);
      return false;
    }
  },

  createTaskCommentWithAttachments: async (taskId: string, content: string, files: File[]) => {
    try {
      // First create the comment
      const comment = await get().createTaskComment(taskId, content);
      if (!comment) throw new Error('Failed to create comment');

      // Upload attachments (max 5)
      const filesToUpload = files.slice(0, 5);
      const uploadedAttachments: TaskCommentAttachment[] = [];

      for (const file of filesToUpload) {
        const attachment = await get().uploadCommentAttachment(comment.id, file);
        if (attachment) {
          uploadedAttachments.push(attachment);
        }
      }

      // Return comment with attachments
      return {
        ...comment,
        attachments: uploadedAttachments
      };
    } catch (error) {
      console.error('Error creating comment with attachments:', error);
      return null;
    }
  },

  uploadTaskAttachment: async (taskId: string, file: File) => {
    try {
      // Upload file to storage
      const fileName = `${Date.now()}-${file.name}`;
      const filePath = `tasks/${taskId}/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      if (!urlData.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      // Create attachment record
      const { data: attachment, error: dbError } = await supabase
        .from('task_attachments')
        .insert({
          task_id: taskId,
          name: file.name,
          url: urlData.publicUrl,
          type: file.type,
          size: file.size,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Update local state
      const tasks = get().tasks.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            attachments: [...(task.attachments || []), attachment]
          };
        }
        return task;
      });

      set({ tasks });
      return attachment;
    } catch (error) {
      console.error('Error uploading attachment:', error);
      return null;
    }
  },

  deleteTaskAttachment: async (attachmentId: string) => {
    try {
      // Get the attachment to get the file path
      const { data: attachment, error: getError } = await supabase
        .from('task_attachments')
        .select('url')
        .eq('id', attachmentId)
        .single();

      if (getError) throw getError;

      // Extract the file path from the URL
      const url = new URL(attachment.url);
      const filePath = url.pathname.split('/').slice(-2).join('/');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('task_attachments')
        .delete()
        .eq('id', attachmentId);

      if (dbError) throw dbError;

      // Update local state
      const tasks = get().tasks.map(task => {
        if (task.attachments?.some(a => a.id === attachmentId)) {
          return {
            ...task,
            attachments: task.attachments.filter(a => a.id !== attachmentId)
          };
        }
        return task;
      });

      set({ tasks });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  },

  // Task checklist methods
  createTaskChecklistItem: async (taskId: string, title: string, description?: string, sortOrder?: number) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Get user's organization_id
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        throw new Error('User organization not found');
      }

      const { data, error } = await supabase
        .from('task_checklist')
        .insert({
          task_id: taskId,
          title,
          description,
          sort_order: sortOrder ?? 0,
          organization_id: userProfile.organization_id,
          created_by: userData.user.id
        })
        .select()
        .single();

      if (error) {
        // Se la tabella non esiste ancora, restituisci null silently
        if (error.code === 'PGRST116' || error.message.includes('task_checklist')) {
          console.warn('task_checklist table does not exist yet');
          return null;
        }
        throw error;
      }

      // Track checklist item addition
      const { createActivityComment } = get();
      createActivityComment(taskId, 'checklist_item_added', { itemTitle: title });

      // Update local state immediately after successful creation
      const tasks = get().tasks.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            checklist: [...(task.checklist || []), data]
          };
        }
        return task;
      });

      set({ tasks });
      console.log('✅ ProjectStore: Created checklist item and updated local state');
      return data;
    } catch (error) {
      console.error('Error creating checklist item:', error);
      return null;
    }
  },

  updateTaskChecklistItem: async (checklistId: string, updates: any) => {
    try {
      const { error } = await supabase
        .from('task_checklist')
        .update(updates)
        .eq('id', checklistId);

      if (error) {
        if (error.code === 'PGRST116' || error.message.includes('task_checklist')) {
          console.warn('task_checklist table does not exist yet');
          return;
        }
        throw error;
      }

      // Update local state
      const tasks = get().tasks.map(task => ({
        ...task,
        checklist: task.checklist?.map(item => 
          item.id === checklistId ? { ...item, ...updates } : item
        )
      }));

      set({ tasks });
    } catch (error) {
      console.error('Error updating checklist item:', error);
      throw error;
    }
  },

  deleteTaskChecklistItem: async (checklistId: string) => {
    try {
      // Find the checklist item to get its title for activity tracking
      const tasks = get().tasks;
      let itemTitle = '';
      let taskId = '';
      
      for (const task of tasks) {
        const item = task.checklist?.find(i => i.id === checklistId);
        if (item) {
          itemTitle = item.title;
          taskId = task.id;
          break;
        }
      }

      const { error } = await supabase
        .from('task_checklist')
        .delete()
        .eq('id', checklistId);

      if (error) {
        if (error.code === 'PGRST116' || error.message.includes('task_checklist')) {
          console.warn('task_checklist table does not exist yet');
          return;
        }
        throw error;
      }

      // Track checklist item removal
      if (taskId) {
        const { createActivityComment } = get();
        createActivityComment(taskId, 'checklist_item_removed', { itemTitle });
      }

      // Update local state
      const updatedTasks = get().tasks.map(task => ({
        ...task,
        checklist: task.checklist?.filter(item => item.id !== checklistId)
      }));

      set({ tasks: updatedTasks });
    } catch (error) {
      console.error('Error deleting checklist item:', error);
      throw error;
    }
  },

  toggleChecklistItem: async (checklistId: string, isCompleted: boolean) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Find the checklist item to get its title and task ID for activity tracking
      const tasks = get().tasks;
      let itemTitle = '';
      let taskId = '';
      
      for (const task of tasks) {
        const item = task.checklist?.find(i => i.id === checklistId);
        if (item) {
          itemTitle = item.title;
          taskId = task.id;
          break;
        }
      }

      const updates = {
        is_completed: isCompleted,
        completed_at: isCompleted ? new Date().toISOString() : null,
        completed_by: isCompleted ? userData.user.id : null
      };

      // AGGIORNAMENTO OTTIMISTICO - aggiorna subito l'UI
      const updatedTasks = get().tasks.map(task => ({
        ...task,
        checklist: task.checklist?.map(item => 
          item.id === checklistId ? { ...item, ...updates } : item
        )
      }));
      set({ tasks: updatedTasks });

      // Poi aggiorna il database
      const { error } = await supabase
        .from('task_checklist')
        .update(updates)
        .eq('id', checklistId);

      if (error) {
        // ROLLBACK: ripristina lo stato precedente in caso di errore
        const originalTasks = get().tasks.map(task => ({
          ...task,
          checklist: task.checklist?.map(item => 
            item.id === checklistId ? { 
              ...item, 
              is_completed: !isCompleted,
              completed_at: !isCompleted ? new Date().toISOString() : null,
              completed_by: !isCompleted ? userData.user.id : null
            } : item
          )
        }));
        set({ tasks: originalTasks });
        
        if (error.code === 'PGRST116' || error.message.includes('task_checklist')) {
          alert('La funzionalità checklist non è ancora attiva. Eseguire la migrazione database.');
          return;
        }
        throw error;
      } else {
        // Track checklist item completion/uncompltion
        if (taskId) {
          const { createActivityComment } = get();
          createActivityComment(
            taskId, 
            isCompleted ? 'checklist_item_completed' : 'checklist_item_uncompleted',
            { itemTitle }
          );
        }
      }
    } catch (error) {
      console.error('Error toggling checklist item:', error);
      throw error;
    }
  },

  // Renewal task methods
  fetchRenewalTasks: async () => {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          assignee:user_profiles!tasks_assigned_to_fkey (
            id,
            full_name,
            email
          )
        `)
        .like('title', '%rinnovo%')
        .neq('status', 'completed')
        .order('due_date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching renewal tasks:', error);
      return [];
    }
  },

  completeRenewalTask: async (taskId: string, dealServiceId: string) => {
    try {
      // Marca la task come completata
      const { error: taskError } = await supabase
        .from('tasks')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', taskId);

      if (taskError) throw taskError;

      // Aggiorna lo stato del contratto come rinnovato
      const { error: renewalError } = await supabase
        .from('deal_services')
        .update({ 
          renewal_status: 'renewed',
          renewal_task_created: false // Permette di creare nuove task se necessario
        })
        .eq('id', dealServiceId);

      if (renewalError) throw renewalError;

      // Aggiorna lo stato locale
      const tasks = get().tasks.map(task => 
        task.id === taskId 
          ? { ...task, status: 'completed', completed_at: new Date().toISOString() }
          : task
      );
      set({ tasks });
    } catch (error) {
      console.error('Error completing renewal task:', error);
      throw error;
    }
  },

  // Activity tracking
  createActivityComment: async (taskId: string, activityType: string, details: any) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      const commentContent = createActivityComment(activityType, details, userData.user);

      const insertData = {
        task_id: taskId,
        content: commentContent,
        created_by: userData.user.id
      };

      // Aggiungi is_system_comment solo se la colonna esiste
      try {
        const { data, error } = await supabase
          .from('task_comments')
          .insert({
            ...insertData,
            is_system_comment: true
          })
          .select(`
            id,
            content,
            created_at,
            is_system_comment,
            author:user_profiles!task_comments_created_by_fkey (
              id,
              email,
              full_name
            )
          `)
          .single();

        if (error) throw error;

        // Update local state
        const tasks = get().tasks.map(task => {
          if (task.id === taskId) {
            return {
              ...task,
              comments: [...(task.comments || []), data]
            };
          }
          return task;
        });

        set({ tasks });
      } catch (error: any) {
        // Fallback senza is_system_comment se la colonna non esiste
        if (error?.message?.includes('is_system_comment') || error?.code === '42703') {
          console.warn('Column is_system_comment not found, falling back without it');

          const { data, error: fallbackError } = await supabase
            .from('task_comments')
            .insert(insertData)
            .select(`
              id,
              content,
              created_at,
              author:user_profiles!task_comments_created_by_fkey (
                id,
                email,
                full_name
              )
            `)
            .single();

          if (fallbackError) throw fallbackError;

          // Update local state
          const tasks = get().tasks.map(task => {
            if (task.id === taskId) {
              return {
                ...task,
                comments: [...(task.comments || []), { ...data, is_system_comment: true }]
              };
            }
            return task;
          });

          set({ tasks });
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error creating activity comment:', error);
    }
  },

  // Project assignment methods
  assignUserToProject: async (projectId: string, userId: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) {
        throw new Error('Organization not found');
      }

      // Get project info for notification
      const { data: projectData } = await supabase
        .from('projects')
        .select('name, client_id')
        .eq('id', projectId)
        .single();

      // Crea assignment
      const { error } = await supabase
        .from('project_assignments')
        .insert({
          project_id: projectId,
          user_id: userId,
          assigned_by: userData.user.id,
          organization_id: profileData.organization_id
        });

      if (error) throw error;

      // Emit notification event for project assignment
      if (projectData) {
        eventBus.emitEvent({
          type: 'project:assigned',
          payload: {
            projectId,
            userId,
            projectName: projectData.name,
            clientId: projectData.client_id
          }
        });
      }

      // Refresh projects to get updated assignments
      await get().fetchProjects();
      // Also refresh selectedProject if viewing this project
      if (get().selectedProject?.id === projectId) {
        await get().fetchProjectById(projectId);
      }
    } catch (error) {
      console.error('Error assigning user to project:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  unassignUserFromProject: async (projectId: string, userId: string) => {
    try {
      const { error } = await supabase
        .from('project_assignments')
        .delete()
        .eq('project_id', projectId)
        .eq('user_id', userId);

      if (error) throw error;

      // Refresh projects to get updated assignments
      await get().fetchProjects();
      // Also refresh selectedProject if viewing this project
      if (get().selectedProject?.id === projectId) {
        await get().fetchProjectById(projectId);
      }
    } catch (error) {
      console.error('Error unassigning user from project:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  fetchProjectAssignments: async (projectId: string) => {
    try {
      const { data, error } = await supabase
        .from('project_assignments')
        .select(`
          *,
          user:user_profiles (
            id,
            email,
            full_name,
            avatar_url
          )
        `)
        .eq('project_id', projectId)
        .order('assigned_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching project assignments:', error);
      return [];
    }
  },

  // Auto-assign user to project if not already a member (fire-and-forget, non-blocking)
  ensureUserAssignedToProject: async (projectId: string, userId: string) => {
    try {
      // Check local store first
      const project = get().projects.find(p => p.id === projectId);
      if (project?.assignments?.some((a: any) => a.user_id === userId)) {
        return; // Already assigned
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profileData?.organization_id) return;

      const { error } = await supabase
        .from('project_assignments')
        .insert({
          project_id: projectId,
          user_id: userId,
          assigned_by: userData.user.id,
          organization_id: profileData.organization_id
        });

      // Ignore duplicate errors (user may have been assigned concurrently)
      if (error && !error.message?.includes('duplicate') && !error.code?.includes('23505')) {
        console.warn('Auto-assign user to project failed:', error.message);
        return;
      }

      // Refresh projects silently
      await get().fetchProjects();
      if (get().selectedProject?.id === projectId) {
        await get().fetchProjectById(projectId);
      }
    } catch (err) {
      console.warn('ensureUserAssignedToProject error:', err);
    }
  },

  // Task JSON export/import methods
  exportProjectTasksAsJson: async (projectId: string) => {
    try {
      const tasks = get().tasks.filter(t => t.project_id === projectId);
      const project = get().selectedProject;

      // Separate sections (headers) and regular tasks
      const sectionHeaders = tasks.filter(t => t.is_header);
      const regularTasks = tasks.filter(t => !t.is_header);

      // Collect unique tag definitions from all tasks
      const tagDefinitionsMap = new Map<string, { name: string; color: string }>();
      regularTasks.forEach(task => {
        (task.tags || []).forEach(tag => {
          if (!tagDefinitionsMap.has(tag.name)) {
            tagDefinitionsMap.set(tag.name, { name: tag.name, color: tag.color });
          }
        });
      });
      const tagDefinitions = Array.from(tagDefinitionsMap.values());

      // Group tasks by section
      const tasksBySection = new Map<string | undefined, typeof regularTasks>();
      regularTasks.forEach(task => {
        const sectionId = task.section;
        if (!tasksBySection.has(sectionId)) {
          tasksBySection.set(sectionId, []);
        }
        tasksBySection.get(sectionId)!.push(task);
      });

      // Build sections array
      const sections: Array<{
        id: string;
        name: string;
        order: number;
        tasks: Array<{
          title: string;
          description?: string;
          status: string;
          priority: string;
          assigned_to?: string;
          due_date?: string;
          tags?: string[];
          checklist: Array<{ title: string; is_required: boolean }>;
        }>;
      }> = [];

      // Sort headers by order and build sections
      sectionHeaders
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .forEach(header => {
          const sectionTasks = tasksBySection.get(header.section) || [];
          sectionTasks.sort((a, b) => (a.order || 0) - (b.order || 0));

          sections.push({
            id: header.section || header.id,
            name: header.title,
            order: header.order || 0,
            tasks: sectionTasks.map(t => ({
              title: t.title,
              description: t.description,
              status: t.status,
              priority: t.priority || 'medium',
              assigned_to: t.assigned_to,
              due_date: t.due_date,
              tags: (t.tags || []).map(tag => tag.name),
              checklist: (t.checklist || []).map(c => ({
                title: c.title,
                is_required: c.is_required
              }))
            }))
          });
        });

      // Tasks without section
      const tasksWithoutSection = tasksBySection.get(undefined) || [];
      tasksWithoutSection.sort((a, b) => (a.order || 0) - (b.order || 0));

      // Fetch organization users for reference
      const { data: userData } = await supabase.auth.getUser();
      let organizationUsers: Array<{ id: string; name: string; email: string }> = [];

      if (userData.user) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();

        if (userProfile?.organization_id) {
          const { data: users } = await supabase
            .from('user_profiles')
            .select('id, full_name, email')
            .eq('organization_id', userProfile.organization_id)
            .eq('active', true) // Escludi gli utenti disattivati
            .order('full_name');

          organizationUsers = (users || []).map(u => ({
            id: u.id,
            name: u.full_name || u.email || 'Utente',
            email: u.email || ''
          }));
        }
      }

      // Schema for reference
      const schema = {
        _description: "Schema delle task del progetto. Prima di generare le task, chiedi all'utente: 1) Come vuole organizzarle: divise per categorie/sezioni come sono attualmente, oppure per sprint di lavoro? 2) Se per sprint: quali task inserire nel primo sprint e quali nei successivi? Come costruire i vari sprint? 3) Se vuole aggiungere o modificare i tag per categorizzare le task. IMPORTANTE: L'output JSON deve mantenere esattamente la stessa struttura di questo template. Non inventare campi nuovi. IMPORTANTE: Non creare tag con lo stesso nome di una sezione. I tag servono per categorizzare trasversalmente (es. Bug, Urgente, Frontend), le sezioni per raggruppare logicamente. Usa tag_definitions per i tag disponibili e organization_users per assegnare le task.",
        description_format: {
          _info: "Le descrizioni delle task usano un editor rich-text (TipTap). Il contenuto DEVE essere in formato HTML. NON usare testo semplice o \\n per gli a capo — verranno ignorati e il testo apparirà tutto su una riga.",
          tags_supportati: ["p", "h1", "h2", "h3", "strong", "em", "ul > li", "ol > li", "a (con href)", "code", "pre > code", "br"],
          regole: [
            "Ogni blocco di testo DEVE essere wrappato in un tag HTML (es. <p>testo</p>)",
            "Per andare a capo: chiudi e riapri <p> (es. </p><p>) oppure usa <br> dentro lo stesso paragrafo",
            "NON usare mai \\n, \\r\\n o testo senza tag HTML",
            "Per liste puntate: <ul><li>elemento</li></ul>",
            "Per grassetto: <strong>testo</strong>, per corsivo: <em>testo</em>"
          ],
          esempio: "<p>Implementare la validazione dei campi del form di registrazione.</p><p>Requisiti:</p><ul><li>Email deve essere valida</li><li>Password minimo 8 caratteri</li></ul><p><strong>Nota:</strong> usare la libreria zod già presente nel progetto.</p>"
        },
        task: {
          title: "string (obbligatorio) - Titolo della task",
          description: "string (HTML TipTap) - Descrizione dettagliata in formato HTML. DEVE usare tag HTML per la formattazione (vedi description_format). Esempio: '<p>Prima riga</p><p>Seconda riga</p>'",
          status: "string - 'todo' | 'in_progress' | 'review' | 'completed'",
          priority: "string - 'low' | 'medium' | 'high' | 'urgent'",
          assigned_to: "string (UUID) - ID utente assegnato (vedi organization_users)",
          due_date: "string - Data scadenza in formato YYYY-MM-DD",
          section: "string - ID della sezione",
          tags: "string[] - Array di nomi tag (vedi tag_definitions per i colori disponibili)",
          checklist: "array - Lista di sotto-elementi"
        },
        tag_definitions: "Array di tag disponibili nel progetto con nome e colore",
        organization_users: organizationUsers
      };

      const exportData = {
        version: '1.1',
        project_id: projectId,
        project_name: project?.name || 'Progetto',
        exported_at: new Date().toISOString(),
        schema,
        tag_definitions: tagDefinitions,
        sections,
        tasks_without_section: tasksWithoutSection.map(t => ({
          title: t.title,
          description: t.description,
          status: t.status,
          priority: t.priority || 'medium',
          assigned_to: t.assigned_to,
          due_date: t.due_date,
          tags: (t.tags || []).map(tag => tag.name),
          checklist: (t.checklist || []).map(c => ({
            title: c.title,
            is_required: c.is_required
          }))
        }))
      };

      // If no tasks, return example JSON
      if (tasks.length === 0) {
        return JSON.stringify({
          version: '1.1',
          project_id: projectId,
          project_name: project?.name || 'Progetto',
          example: true,
          _nota: "Questo è un esempio. Modifica 'sections' e 'tasks_without_section' con le tue task.",
          schema,
          tag_definitions: [
            { name: 'Bug', color: '#ef4444' },
            { name: 'Feature', color: '#3b82f6' }
          ],
          sections: [
            {
              id: 'section_esempio',
              name: 'Sezione Esempio',
              order: 1,
              tasks: [
                {
                  title: 'Task di esempio',
                  description: '<p>Descrizione della task con <strong>formattazione</strong>.</p><p>Secondo paragrafo per spiegare i dettagli.</p>',
                  status: 'todo',
                  priority: 'medium',
                  assigned_to: null,
                  due_date: null,
                  tags: ['Bug'],
                  checklist: [
                    { title: 'Passaggio 1', is_required: true },
                    { title: 'Passaggio 2', is_required: false }
                  ]
                }
              ]
            }
          ],
          tasks_without_section: []
        }, null, 2);
      }

      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error('Error exporting project tasks:', error);
      return JSON.stringify({ error: 'Errore nell\'esportazione' });
    }
  },

  importProjectTasksFromJson: async (
    projectId: string,
    json: string,
    mode: 'replace' | 'append',
    onProgress?: (current: number, total: number) => void
  ) => {
    try {
      // Parse JSON
      let data;
      try {
        data = JSON.parse(json);
      } catch {
        return { success: false, imported: 0, error: 'JSON non valido' };
      }

      // Validate structure
      if (!data.version) {
        return { success: false, imported: 0, error: 'Formato JSON non valido: manca il campo version' };
      }

      // Normalize format: if tasks are in a separate top-level `tasks` array
      // with `section` references (instead of nested inside each section),
      // group them into their respective sections
      const rawSections = data.sections || [];
      const sections: any[] = [];

      if (Array.isArray(data.tasks) && data.tasks.length > 0 && rawSections.length > 0 && !rawSections[0]?.tasks) {
        // Alternate format: sections are definitions, tasks have section references
        const tasksBySection = new Map<string, any[]>();
        const orphanTasks: any[] = [];

        for (const task of data.tasks) {
          if (task.section) {
            if (!tasksBySection.has(task.section)) {
              tasksBySection.set(task.section, []);
            }
            tasksBySection.get(task.section)!.push(task);
          } else {
            orphanTasks.push(task);
          }
        }

        for (const sec of rawSections) {
          sections.push({
            name: sec.title || sec.name || 'Sezione',
            tasks: tasksBySection.get(sec.id) || tasksBySection.get(sec.title) || []
          });
        }

        // Add orphan tasks to tasks_without_section
        if (orphanTasks.length > 0) {
          data.tasks_without_section = [
            ...(data.tasks_without_section || []),
            ...orphanTasks
          ];
        }
      } else {
        // Standard format: tasks nested inside sections
        sections.push(...rawSections);
      }

      // Also collect tags from data.tasks if present (alternate format)
      const allTasksFlat = Array.isArray(data.tasks) ? data.tasks : [];

      // Calculate total items
      let totalItems = 0;
      sections.forEach((section: { tasks?: unknown[] }) => {
        totalItems++; // section header
        totalItems += section.tasks?.length || 0;
      });
      totalItems += (data.tasks_without_section || []).length;

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        return { success: false, imported: 0, error: 'Utente non autenticato' };
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        return { success: false, imported: 0, error: 'Organizzazione non trovata' };
      }

      // Resolve tags: collect all referenced tag names, create missing ones, build name->id map
      const tagNameToId = new Map<string, string>();
      const importedTagDefs = data.tag_definitions || [];

      // Collect all unique tag names referenced in tasks
      const allReferencedTagNames = new Set<string>();
      sections.forEach((section: any) => {
        (section.tasks || []).forEach((task: any) => {
          (task.tags || []).forEach((tagName: string) => allReferencedTagNames.add(tagName));
        });
      });
      (data.tasks_without_section || []).forEach((task: any) => {
        (task.tags || []).forEach((tagName: string) => allReferencedTagNames.add(tagName));
      });
      // Also check flat tasks array (alternate format, already grouped but tags need collecting)
      allTasksFlat.forEach((task: any) => {
        (task.tags || []).forEach((tagName: string) => allReferencedTagNames.add(tagName));
      });

      if (allReferencedTagNames.size > 0) {
        // Fetch existing project tags
        const { data: existingTags } = await supabase
          .from('project_task_tags')
          .select('id, name, color, sort_order')
          .eq('project_id', projectId)
          .eq('is_active', true);

        // Build map of existing tags by lowercase name
        const existingTagMap = new Map<string, { id: string; name: string }>();
        let maxSortOrder = 0;
        (existingTags || []).forEach(t => {
          existingTagMap.set(t.name.toLowerCase(), { id: t.id, name: t.name });
          if (t.sort_order > maxSortOrder) maxSortOrder = t.sort_order;
        });

        // For each referenced tag, either map to existing or create new
        for (const tagName of allReferencedTagNames) {
          const existing = existingTagMap.get(tagName.toLowerCase());
          if (existing) {
            tagNameToId.set(tagName, existing.id);
          } else {
            // Find color from tag_definitions, or use default
            const tagDef = importedTagDefs.find(
              (td: any) => td.name.toLowerCase() === tagName.toLowerCase()
            );
            const color = tagDef?.color || '#6B7280';
            maxSortOrder++;

            const { data: newTag, error: tagError } = await supabase
              .from('project_task_tags')
              .insert({
                project_id: projectId,
                name: tagName,
                color: color,
                sort_order: maxSortOrder,
                is_active: true,
                created_by: userData.user.id
              })
              .select()
              .single();

            if (!tagError && newTag) {
              tagNameToId.set(tagName, newTag.id);
            }
          }
        }
      }

      // If replace mode, archive existing tasks instead of deleting
      if (mode === 'replace') {
        const existingTasks = get().tasks.filter(t => t.project_id === projectId);

        if (existingTasks.length > 0) {
          // Find or create "Task archiviate" section
          const archiveSectionName = 'Task archiviate';
          let archiveSectionId: string | null = null;

          const existingArchiveHeader = existingTasks.find(
            t => t.is_header && t.title === archiveSectionName
          );

          if (existingArchiveHeader) {
            archiveSectionId = existingArchiveHeader.section || existingArchiveHeader.id;
          } else {
            const archiveSectionSlug = `section_task_archiviate_${Date.now()}`;
            const maxOrder = existingTasks
              .filter(t => t.is_header)
              .reduce((max, t) => Math.max(max, t.order || 0), 0);

            const { data: archiveHeader, error: archiveError } = await supabase
              .from('tasks')
              .insert({
                project_id: projectId,
                title: archiveSectionName,
                status: 'todo',
                priority: 'medium',
                is_header: true,
                section: archiveSectionSlug,
                order: maxOrder + 1,
                created_by: userData.user.id
              })
              .select()
              .single();

            if (archiveError) {
              console.error('Error creating archive section:', archiveError);
              return { success: false, imported: 0, error: 'Errore nella creazione della sezione archivio' };
            }

            archiveSectionId = archiveSectionSlug;
          }

          // Move all regular tasks to the archive section (batch update)
          const tasksToArchive = existingTasks.filter(t =>
            !t.is_header || (t.is_header && t.title !== archiveSectionName)
          );
          const regularTaskIds = tasksToArchive.filter(t => !t.is_header).map(t => t.id);
          const oldHeaderIds = tasksToArchive.filter(t => t.is_header).map(t => t.id);

          if (regularTaskIds.length > 0) {
            await supabase
              .from('tasks')
              .update({ section: archiveSectionId })
              .in('id', regularTaskIds);
          }

          // Delete old section headers (they are empty now)
          if (oldHeaderIds.length > 0) {
            await supabase
              .from('tasks')
              .delete()
              .in('id', oldHeaderIds);
          }
        }
      }

      let imported = 0;
      let currentProgress = 0;

      // Import sections
      for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
        const section = sections[sectionIndex];
        const sectionId = `section_${section.name?.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}_${sectionIndex}`;

        // Create section header
        const { data: headerData, error: headerError } = await supabase
          .from('tasks')
          .insert({
            project_id: projectId,
            title: section.name || 'Sezione',
            status: 'todo',
            priority: 'medium',
            is_header: true,
            section: sectionId,
            order: sectionIndex,
            created_by: userData.user.id
          })
          .select()
          .single();

        if (headerError) {
          console.error('Error creating section header:', headerError);
          continue;
        }

        imported++;
        currentProgress++;
        onProgress?.(currentProgress, totalItems);

        // Create tasks in this section
        const sectionTasks = section.tasks || [];
        for (let taskIndex = 0; taskIndex < sectionTasks.length; taskIndex++) {
          const taskData = sectionTasks[taskIndex];

          const insertPayload = {
              project_id: projectId,
              title: taskData.title,
              description: taskData.description || null,
              status: taskData.status || 'todo',
              priority: taskData.priority || 'medium',
              assigned_to: taskData.assigned_to || null,
              due_date: taskData.due_date || null,
              completed_at: taskData.status === 'completed' ? new Date().toISOString() : null,
              section: sectionId,
              order: (sectionIndex * 100) + taskIndex,
              created_by: userData.user.id
            };
          console.log('[IMPORT DEBUG] Task insert payload:', taskData.title, '| status:', insertPayload.status, '| desc length:', insertPayload.description?.length, '| desc preview:', insertPayload.description?.substring(0, 80));

          const { data: newTask, error: taskError } = await supabase
            .from('tasks')
            .insert(insertPayload)
            .select()
            .single();

          if (taskError) {
            console.error('Error creating task:', taskError);
            continue;
          }
          console.log('[IMPORT DEBUG] Task created:', newTask.title, '| DB status:', newTask.status, '| DB desc length:', newTask.description?.length);

          // Create checklist items
          if (taskData.checklist && taskData.checklist.length > 0) {
            for (let i = 0; i < taskData.checklist.length; i++) {
              const checklistItem = taskData.checklist[i];
              await supabase.from('task_checklist').insert({
                task_id: newTask.id,
                title: checklistItem.title,
                is_required: checklistItem.is_required || false,
                sort_order: i,
                organization_id: userProfile.organization_id,
                created_by: userData.user.id
              });
            }
          }

          // Assign tags to this task
          if (taskData.tags && taskData.tags.length > 0) {
            const tagIdsForTask = taskData.tags
              .map((tagName: string) => tagNameToId.get(tagName))
              .filter(Boolean) as string[];

            if (tagIdsForTask.length > 0) {
              await supabase
                .from('task_tag_assignments')
                .insert(tagIdsForTask.map(tagId => ({
                  task_id: newTask.id,
                  tag_id: tagId,
                  assigned_by: userData.user.id
                })));
            }
          }

          imported++;
          currentProgress++;
          onProgress?.(currentProgress, totalItems);
        }
      }

      // Import tasks without section
      const tasksWithoutSection = data.tasks_without_section || [];
      for (let taskIndex = 0; taskIndex < tasksWithoutSection.length; taskIndex++) {
        const taskData = tasksWithoutSection[taskIndex];

        const insertPayload2 = {
            project_id: projectId,
            title: taskData.title,
            description: taskData.description || null,
            status: taskData.status || 'todo',
            priority: taskData.priority || 'medium',
            assigned_to: taskData.assigned_to || null,
            due_date: taskData.due_date || null,
            completed_at: taskData.status === 'completed' ? new Date().toISOString() : null,
            order: (sections.length * 100) + taskIndex,
            created_by: userData.user.id
          };
        console.log('[IMPORT DEBUG no-section] Task insert:', taskData.title, '| status:', insertPayload2.status, '| desc length:', insertPayload2.description?.length);

        const { data: newTask, error: taskError } = await supabase
          .from('tasks')
          .insert(insertPayload2)
          .select()
          .single();

        if (taskError) {
          console.error('Error creating task:', taskError);
          continue;
        }
        console.log('[IMPORT DEBUG no-section] Task created:', newTask.title, '| DB status:', newTask.status);

        // Create checklist items
        if (taskData.checklist && taskData.checklist.length > 0) {
          for (let i = 0; i < taskData.checklist.length; i++) {
            const checklistItem = taskData.checklist[i];
            await supabase.from('task_checklist').insert({
              task_id: newTask.id,
              title: checklistItem.title,
              is_required: checklistItem.is_required || false,
              sort_order: i,
              organization_id: userProfile.organization_id,
              created_by: userData.user.id
            });
          }
        }

        // Assign tags to this task
        if (taskData.tags && taskData.tags.length > 0) {
          const tagIdsForTask = taskData.tags
            .map((tagName: string) => tagNameToId.get(tagName))
            .filter(Boolean) as string[];

          if (tagIdsForTask.length > 0) {
            await supabase
              .from('task_tag_assignments')
              .insert(tagIdsForTask.map(tagId => ({
                task_id: newTask.id,
                tag_id: tagId,
                assigned_by: userData.user.id
              })));
          }
        }

        imported++;
        currentProgress++;
        onProgress?.(currentProgress, totalItems);
      }

      // Refresh tasks
      await get().fetchProjectTasks(projectId);

      return { success: true, imported };
    } catch (error) {
      console.error('Error importing project tasks:', error);
      return { success: false, imported: 0, error: friendlyDbError(error) };
    }
  },

  createTasksBulk: async (
    projectId: string,
    tasks: Array<{
      title: string;
      description?: string;
      status?: string;
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      assigned_to?: string;
      due_date?: string;
      section?: string;
      checklist?: Array<{ title: string; is_required: boolean }>;
      tags?: string[];
    }>,
    onProgress?: (current: number, total: number) => void
  ) => {
    const errors: string[] = [];
    let created = 0;

    // Colori predefiniti per tag generati dall'AI
    const AI_TAG_COLORS: Record<string, string> = {
      'setup': '#3B82F6',
      'analisi': '#8B5CF6',
      'design': '#EC4899',
      'sviluppo frontend': '#F59E0B',
      'sviluppo backend': '#10B981',
      'contenuti': '#6366F1',
      'testing/qa': '#EF4444',
      'deploy': '#14B8A6',
      'post-lancio': '#F97316',
    };

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        return { success: false, created: 0, errors: ['Utente non autenticato'] };
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        return { success: false, created: 0, errors: ['Organizzazione non trovata'] };
      }

      // Prepare tag map if any task has tags
      const allTagNames = new Set<string>();
      tasks.forEach(t => t.tags?.forEach(tag => allTagNames.add(tag)));
      const tagNameToId = new Map<string, string>();

      if (allTagNames.size > 0) {
        // Fetch existing project tags
        const { data: existingTags } = await supabase
          .from('project_task_tags')
          .select('id, name, sort_order')
          .eq('project_id', projectId)
          .eq('is_active', true);

        let maxSortOrder = 0;
        (existingTags || []).forEach(t => {
          tagNameToId.set(t.name.toLowerCase(), t.id);
          if (t.sort_order > maxSortOrder) maxSortOrder = t.sort_order;
        });

        // Create missing tags
        for (const tagName of allTagNames) {
          if (!tagNameToId.has(tagName.toLowerCase())) {
            maxSortOrder++;
            const color = AI_TAG_COLORS[tagName.toLowerCase()] || '#6B7280';
            const { data: newTag } = await supabase
              .from('project_task_tags')
              .insert({
                project_id: projectId,
                name: tagName,
                color,
                sort_order: maxSortOrder,
                is_active: true,
                created_by: userData.user.id
              })
              .select()
              .single();

            if (newTag) {
              tagNameToId.set(tagName.toLowerCase(), newTag.id);
            }
          }
        }
      }

      // Get existing sections for the project
      const existingTasks = get().tasks.filter(t => t.project_id === projectId);
      const existingSections = existingTasks.filter(t => t.is_header);
      const sectionMap = new Map<string, string>(); // name -> section id

      existingSections.forEach(s => {
        sectionMap.set(s.title.toLowerCase(), s.section || s.id);
      });

      // Get max order
      const maxOrder = Math.max(...existingTasks.map(t => t.order || 0), 0);

      for (let i = 0; i < tasks.length; i++) {
        const taskData = tasks[i];
        let sectionId = taskData.section;

        // If section name provided, find or create section
        if (sectionId && !sectionMap.has(sectionId.toLowerCase())) {
          // Create new section
          const newSectionId = `section_${sectionId.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;
          const { error: sectionError } = await supabase
            .from('tasks')
            .insert({
              project_id: projectId,
              title: sectionId,
              status: 'todo',
              priority: 'medium',
              is_header: true,
              section: newSectionId,
              order: maxOrder + (i * 2),
              created_by: userData.user.id
            });

          if (!sectionError) {
            sectionMap.set(sectionId.toLowerCase(), newSectionId);
            sectionId = newSectionId;
          }
        } else if (sectionId) {
          sectionId = sectionMap.get(sectionId.toLowerCase());
        }

        // Create task
        const { data: newTask, error: taskError } = await supabase
          .from('tasks')
          .insert({
            project_id: projectId,
            title: taskData.title,
            description: taskData.description,
            status: taskData.status || 'todo',
            priority: taskData.priority || 'medium',
            assigned_to: taskData.assigned_to,
            due_date: taskData.due_date,
            section: sectionId,
            order: maxOrder + (i * 2) + 1,
            created_by: userData.user.id
          })
          .select()
          .single();

        if (taskError) {
          errors.push(`Task "${taskData.title}": ${taskError.message}`);
          continue;
        }

        // Create checklist items
        if (taskData.checklist && taskData.checklist.length > 0 && newTask) {
          for (let j = 0; j < taskData.checklist.length; j++) {
            const checklistItem = taskData.checklist[j];
            const { error: checklistError } = await supabase
              .from('task_checklist')
              .insert({
                task_id: newTask.id,
                title: checklistItem.title,
                is_required: checklistItem.is_required || false,
                sort_order: j,
                organization_id: userProfile.organization_id,
                created_by: userData.user.id
              });

            if (checklistError) {
              console.warn(`Checklist item error for task "${taskData.title}":`, checklistError);
            }
          }
        }

        // Assign tags to this task
        if (taskData.tags && taskData.tags.length > 0 && newTask) {
          const tagIds = taskData.tags
            .map(tagName => tagNameToId.get(tagName.toLowerCase()))
            .filter(Boolean) as string[];

          if (tagIds.length > 0) {
            await supabase
              .from('task_tag_assignments')
              .insert(tagIds.map(tagId => ({
                task_id: newTask.id,
                tag_id: tagId,
                assigned_by: userData.user.id
              })));
          }
        }

        created++;
        onProgress?.(created, tasks.length);
      }

      // Refresh tasks
      await get().fetchProjectTasks(projectId);

      return { success: errors.length === 0, created, errors };
    } catch (error) {
      console.error('Error creating tasks bulk:', error);
      return { success: false, created, errors: [...errors, friendlyDbError(error)] };
    }
  },

  // Update multiple tasks in bulk (for AI improvements)
  updateTasksBulk: async (projectId, updates) => {
    const errors: string[] = [];
    let updated = 0;

    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) throw new Error('Organization not found');

      for (const update of updates) {
        try {
          // Update task main fields (title, description)
          const taskUpdate: Record<string, any> = {};
          if (update.title !== undefined) taskUpdate.title = update.title;
          if (update.description !== undefined) taskUpdate.description = update.description;

          if (Object.keys(taskUpdate).length > 0) {
            const { error: updateError } = await supabase
              .from('tasks')
              .update(taskUpdate)
              .eq('id', update.id)
              .eq('project_id', projectId);

            if (updateError) {
              errors.push(`Errore aggiornamento task ${update.id}: ${updateError.message}`);
              continue;
            }
          }

          // Update checklist if provided
          if (update.checklist !== undefined) {
            // Delete existing checklist items
            await supabase
              .from('task_checklist')
              .delete()
              .eq('task_id', update.id);

            // Insert new checklist items
            if (update.checklist.length > 0) {
              const checklistItems = update.checklist.map((item, index) => ({
                task_id: update.id,
                title: item.title,
                is_required: item.is_required || false,
                sort_order: index,
                organization_id: profileData.organization_id,
                created_by: userData.user.id
              }));

              const { error: checklistError } = await supabase
                .from('task_checklist')
                .insert(checklistItems);

              if (checklistError) {
                errors.push(`Errore checklist task ${update.id}: ${checklistError.message}`);
                continue;
              }
            }
          }

          updated++;
        } catch (taskError) {
          errors.push(`Errore task ${update.id}: ${(taskError as Error).message}`);
        }
      }

      // Refresh tasks
      await get().fetchProjectTasks(projectId);

      return { success: errors.length === 0, updated, errors };
    } catch (error) {
      console.error('Error updating tasks bulk:', error);
      return { success: false, updated, errors: [...errors, friendlyDbError(error)] };
    }
  },

  // Quick inline update for single fields with optimistic update
  updateProjectQuick: async (id, field, value) => {
    try {
      // Optimistic update
      const previousProjects = get().projects;
      set({
        projects: previousProjects.map(p =>
          p.id === id ? { ...p, [field]: value } : p
        )
      });

      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) throw new Error('Organization not found');

      const updateData: Record<string, any> = {};
      updateData[field] = value;

      const { error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', id)
        .eq('organization_id', profileData.organization_id);

      if (error) {
        // Rollback on error
        set({ projects: previousProjects });
        throw error;
      }

      // Refresh to get joined data (PM profile, etc.)
      await get().fetchProjects(get().showArchived);
      return true;
    } catch (error) {
      console.error('Error in updateProjectQuick:', error);
      return false;
    }
  },

  // Quick update health status by creating a new status update
  updateHealthStatusQuick: async (projectId, healthStatus) => {
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) throw new Error('Organization not found');

      // Create new status update
      const { error } = await supabase
        .from('project_status_updates')
        .insert({
          project_id: projectId,
          organization_id: profileData.organization_id,
          health_status: healthStatus,
          notes: 'Aggiornamento rapido da lista',
          created_by: userData.user.id
        });

      if (error) throw error;

      // Optimistic update for immediate UI feedback
      const previousProjects = get().projects;
      const project = previousProjects.find(p => p.id === projectId);
      set({
        projects: previousProjects.map(p =>
          p.id === projectId
            ? {
                ...p,
                latest_status_update: {
                  health_status: healthStatus,
                  created_at: new Date().toISOString()
                }
              }
            : p
        )
      });

      // Emit evento per notifica
      eventBus.emitEvent({
        type: 'project:status_update',
        payload: {
          projectId,
          projectName: project?.name || '',
          statusUpdateId: '',
          healthStatus,
          isEdit: false,
        }
      });

      return true;
    } catch (error) {
      console.error('Error in updateHealthStatusQuick:', error);
      return false;
    }
  },

  // Brief Documents Methods
  fetchBriefDocuments: async (projectId: string) => {
    try {
      const { data, error } = await supabase
        .from('project_brief_documents')
        .select('*')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      set({ briefDocuments: data || [] });
      return data || [];
    } catch (error) {
      console.error('Error fetching brief documents:', error);
      return [];
    }
  },

  uploadBriefDocument: async (projectId: string, file: File) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userId)
        .single();

      if (!profile?.organization_id) throw new Error('No organization found');

      // Upload file to storage
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/\s+/g, '_');
      const filePath = `brief/${projectId}/${timestamp}-${sanitizedName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      if (!urlData.publicUrl) throw new Error('Failed to get public URL');

      // Get max sort_order
      const { data: maxSortData } = await supabase
        .from('project_brief_documents')
        .select('sort_order')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: false })
        .limit(1)
        .single();

      const nextSortOrder = (maxSortData?.sort_order ?? -1) + 1;

      // Create database record
      const { data: document, error: dbError } = await supabase
        .from('project_brief_documents')
        .insert({
          organization_id: profile.organization_id,
          project_id: projectId,
          name: file.name,
          original_name: file.name,
          url: urlData.publicUrl,
          file_path: filePath,
          type: file.type,
          size: file.size,
          document_type: 'file',
          visible_to_client: false,
          sort_order: nextSortOrder,
          created_by: userId
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Update local state
      set({ briefDocuments: [...get().briefDocuments, document] });

      return document;
    } catch (error) {
      console.error('Error uploading brief document:', error);
      return null;
    }
  },

  addBriefLink: async (projectId: string, name: string, url: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userId)
        .single();

      if (!profile?.organization_id) throw new Error('No organization found');

      // Get max sort_order
      const { data: maxSortData } = await supabase
        .from('project_brief_documents')
        .select('sort_order')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: false })
        .limit(1)
        .single();

      const nextSortOrder = (maxSortData?.sort_order ?? -1) + 1;

      // Create database record
      const { data: document, error: dbError } = await supabase
        .from('project_brief_documents')
        .insert({
          organization_id: profile.organization_id,
          project_id: projectId,
          name: name,
          url: url,
          document_type: 'link',
          visible_to_client: false,
          sort_order: nextSortOrder,
          created_by: userId
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Update local state
      set({ briefDocuments: [...get().briefDocuments, document] });

      return document;
    } catch (error) {
      console.error('Error adding brief link:', error);
      return null;
    }
  },

  renameBriefDocument: async (documentId: string, newName: string) => {
    try {
      const { error } = await supabase
        .from('project_brief_documents')
        .update({ name: newName, updated_at: new Date().toISOString() })
        .eq('id', documentId);

      if (error) throw error;

      // Update local state
      const documents = get().briefDocuments.map(doc =>
        doc.id === documentId ? { ...doc, name: newName } : doc
      );
      set({ briefDocuments: documents });
    } catch (error) {
      console.error('Error renaming document:', error);
      throw error;
    }
  },

  toggleBriefDocumentVisibility: async (documentId: string, visible: boolean) => {
    try {
      const { error } = await supabase
        .from('project_brief_documents')
        .update({ visible_to_client: visible, updated_at: new Date().toISOString() })
        .eq('id', documentId);

      if (error) throw error;

      // Update local state
      const documents = get().briefDocuments.map(doc =>
        doc.id === documentId ? { ...doc, visible_to_client: visible } : doc
      );
      set({ briefDocuments: documents });
    } catch (error) {
      console.error('Error toggling document visibility:', error);
      throw error;
    }
  },

  deleteBriefDocument: async (documentId: string) => {
    try {
      const document = get().briefDocuments.find(d => d.id === documentId);
      if (!document) throw new Error('Document not found');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([document.file_path]);

      if (storageError) console.warn('Storage delete warning:', storageError);

      // Delete from database
      const { error: dbError } = await supabase
        .from('project_brief_documents')
        .delete()
        .eq('id', documentId);

      if (dbError) throw dbError;

      // Update local state
      set({ briefDocuments: get().briefDocuments.filter(d => d.id !== documentId) });
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  reorderBriefDocuments: async (projectId: string, documentIds: string[]) => {
    try {
      // Update sort_order for each document
      for (let i = 0; i < documentIds.length; i++) {
        await supabase
          .from('project_brief_documents')
          .update({ sort_order: i })
          .eq('id', documentIds[i]);
      }

      // Update local state maintaining order
      const orderedDocs = documentIds
        .map(id => get().briefDocuments.find(d => d.id === id))
        .filter((doc): doc is ProjectBriefDocument => doc !== undefined)
        .map((doc, index) => ({ ...doc, sort_order: index }));

      set({ briefDocuments: orderedDocs });
    } catch (error) {
      console.error('Error reordering documents:', error);
      throw error;
    }
  },

  // Client assignment methods
  fetchProjectClientAccesses: async (projectId: string) => {
    try {
      // Fetch client accesses that have access to this project, including shared_sections
      const { data, error } = await supabase
        .from('client_portal_projects')
        .select(`
          shared_sections,
          client_access:client_portal_access!inner(
            id,
            client_id,
            username,
            is_active,
            client:clients(
              id,
              business_name
            )
          )
        `)
        .eq('project_id', projectId);

      if (error) throw error;

      // Flatten the result and filter active accesses
      const activeRecords = (data || []).filter(
        item => item.client_access && (item.client_access as any).is_active
      );

      const accesses = activeRecords.map(item => {
        const access = item.client_access as any;
        return {
          id: access.id,
          client_id: access.client_id,
          username: access.username,
          client: access.client ? { business_name: access.client.business_name } : undefined
        };
      });

      // Aggregate shared sections from all active accesses
      const sharedSections = new Set<string>();
      for (const record of activeRecords) {
        if (record.shared_sections) {
          (record.shared_sections as string[]).forEach(s => sharedSections.add(s));
        }
      }

      set({ projectClientAccesses: accesses, portalSharedSections: sharedSections });
    } catch (error) {
      console.error('Error fetching project client accesses:', error);
      set({ projectClientAccesses: [], portalSharedSections: new Set() });
    }
  },

  fetchProjectCollaboratorAccesses: async (_projectId: string) => {
    try {
      // Fetch ALL active collaborator accesses for the organization (not just project-specific)
      // so that any collaborator can be assigned tasks in any project
      const { data, error } = await supabase
        .from('collaborator_portal_access')
        .select(`
          id,
          external_partner_id,
          username,
          preferred_language,
          is_active,
          partner:external_partners(id, name, company)
        `)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching collaborator accesses:', error);
        set({ projectCollaboratorAccesses: [] });
        return;
      }

      const accesses = (data || []).map((d: any) => ({
        id: d.id,
        external_partner_id: d.external_partner_id,
        username: d.username,
        preferred_language: d.preferred_language || 'it',
        partner: d.partner ? {
          id: d.partner.id,
          name: d.partner.name,
          company: d.partner.company
        } : undefined
      }));

      set({ projectCollaboratorAccesses: accesses });
    } catch (error) {
      console.error('Error fetching collaborator accesses:', error);
      set({ projectCollaboratorAccesses: [] });
    }
  },

  toggleSectionPortalSharing: async (projectId: string, sectionId: string) => {
    try {
      const { ClientAuthService } = await import('../services/clientAuthService');
      const updatedSections = await ClientAuthService.toggleSectionPortalAccess(projectId, sectionId);
      set({ portalSharedSections: new Set(updatedSections) });
    } catch (error) {
      console.error('Error toggling section portal sharing:', error);
    }
  },

  assignTaskToClient: async (taskId: string, clientAccessId: string | null) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ assigned_client_access_id: clientAccessId })
        .eq('id', taskId);

      if (error) throw error;

      // Update local state
      set(state => ({
        tasks: state.tasks.map(t =>
          t.id === taskId
            ? { ...t, assigned_client_access_id: clientAccessId || undefined }
            : t
        )
      }));
    } catch (error) {
      console.error('Error assigning task to client:', error);
      throw error;
    }
  },

  bulkAssignTasksToClient: async (taskIds: string[], clientAccessId: string | null) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ assigned_client_access_id: clientAccessId })
        .in('id', taskIds);

      if (error) throw error;

      // Update local state
      set(state => ({
        tasks: state.tasks.map(t =>
          taskIds.includes(t.id)
            ? { ...t, assigned_client_access_id: clientAccessId || undefined }
            : t
        )
      }));
    } catch (error) {
      console.error('Error bulk assigning tasks to client:', error);
      throw error;
    }
  },

  // Client comments methods
  fetchTaskClientComments: async (taskId: string) => {
    set({ taskClientCommentsLoading: true });
    try {
      const { data, error } = await supabase
        .from('client_task_comments')
        .select(`
          *,
          attachments:client_task_comment_attachments (
            id, comment_id, name, url, type, size, uploader_type, uploader_id, created_at
          )
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      set({
        taskClientComments: data || [],
        taskClientCommentsLoading: false
      });
    } catch (error) {
      console.error('Error fetching client comments:', error);
      set({ taskClientComments: [], taskClientCommentsLoading: false });
    }
  },

  replyToClientComment: async (taskId: string, content: string, authorName: string, notifyClient = false) => {
    console.log('[replyToClientComment] Inizio invio risposta per task:', taskId, 'notifyClient:', notifyClient);
    try {
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');
      console.log('[replyToClientComment] User autenticato:', userData.user.id);

      // Get organization_id and project info from task
      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .select('title, project:projects(id, organization_id, name)')
        .eq('id', taskId)
        .single();

      console.log('[replyToClientComment] Task data:', taskData, 'Error:', taskError);
      if (taskError) throw taskError;

      const organizationId = (taskData?.project as any)?.organization_id;
      if (!organizationId) throw new Error('Could not find organization');

      console.log('[replyToClientComment] Inserisco commento con org_id:', organizationId);
      const { data, error } = await supabase
        .from('client_task_comments')
        .insert({
          organization_id: organizationId,
          task_id: taskId,
          content: content.trim(),
          author_type: 'staff',
          author_id: userData.user.id,
          author_name: authorName,
          is_read: true // Staff comments are always read
        })
        .select()
        .single();

      if (error) {
        console.error('[replyToClientComment] Errore insert:', error);
        throw error;
      }

      console.log('[replyToClientComment] Commento inserito:', data);

      // Add to local state
      set(state => ({
        taskClientComments: [...state.taskClientComments, data]
      }));

      // Notify client if toggle is ON
      if (notifyClient) {
        try {
          const projectId = (taskData?.project as any)?.id;
          const projectName = (taskData?.project as any)?.name || '';
          const taskTitle = taskData?.title || '';

          // Get all client_portal_access IDs that have access to this project
          const { data: portalProjects } = await supabase
            .from('client_portal_projects')
            .select('client_access_id')
            .eq('project_id', projectId);

          if (portalProjects && portalProjects.length > 0) {
            // Send notification to each client with access
            for (const pp of portalProjects) {
              supabase.functions.invoke('create-client-notification', {
                body: {
                  organization_id: organizationId,
                  client_access_id: pp.client_access_id,
                  event_type: 'task_staff_reply',
                  data: {
                    title: `Nuova risposta su "${taskTitle}"`,
                    message: content.trim().length > 150 ? content.trim().substring(0, 150) + '...' : content.trim(),
                    link: `/portal/projects/${projectId}?task=${taskId}`,
                    task_id: taskId,
                    project_id: projectId,
                    sender_name: authorName,
                    priority: 'normal'
                  },
                  send_email: true
                }
              }).catch(err => console.error('[replyToClientComment] Errore notifica client:', err));
            }
          }
        } catch (notifError) {
          // Non-blocking: non bloccare la reply se la notifica fallisce
          console.error('[replyToClientComment] Errore invio notifica client (non-blocking):', notifError);
        }
      }

      return true;
    } catch (error) {
      console.error('[replyToClientComment] Error:', error);
      return false;
    }
  },

  markClientCommentsAsRead: async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('client_task_comments')
        .update({ is_read: true })
        .eq('task_id', taskId)
        .eq('author_type', 'client')
        .eq('is_read', false);

      if (error) throw error;

      // Update local state
      set(state => ({
        taskClientComments: state.taskClientComments.map(c =>
          c.task_id === taskId && c.author_type === 'client'
            ? { ...c, is_read: true }
            : c
        )
      }));
    } catch (error) {
      console.error('Error marking client comments as read:', error);
    }
  },

  transcribeClientComment: async (commentId: string, audioUrl: string) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${supabaseUrl}/functions/v1/transcribe-audio`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token || ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ audioUrl }),
        }
      );

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const result = await response.json();
      const transcribedText = result.text || '';

      if (!transcribedText) {
        return '';
      }

      // Update comment content in DB
      const { error } = await supabase
        .from('client_task_comments')
        .update({ content: `🎤 Messaggio vocale\n\n📝 Trascrizione:\n${transcribedText}` })
        .eq('id', commentId);

      if (error) throw error;

      // Update local state
      set(state => ({
        taskClientComments: state.taskClientComments.map(c =>
          c.id === commentId
            ? { ...c, content: `🎤 Messaggio vocale\n\n📝 Trascrizione:\n${transcribedText}` }
            : c
        )
      }));

      return transcribedText;
    } catch (error) {
      console.error('Error transcribing client comment:', error);
      throw error;
    }
  },

  // Realtime local update methods (used by taskRealtimeService)
  updateTaskLocally: (taskId: string, updates: Partial<Task>) => {
    set(state => ({
      tasks: state.tasks.map(t =>
        t.id === taskId ? { ...t, ...updates } : t
      )
    }));
  },

  addTaskLocally: (task: Task) => {
    set(state => {
      // Check if task already exists to avoid duplicates
      const exists = state.tasks.some(t => t.id === task.id);
      if (exists) {
        return state;
      }
      return {
        tasks: [...state.tasks, task]
      };
    });
  },

  removeTaskLocally: (taskId: string) => {
    set(state => ({
      tasks: state.tasks.filter(t => t.id !== taskId)
    }));
  },

  addClientCommentLocally: (comment) => {
    set(state => {
      // Check if comment already exists to avoid duplicates
      const exists = state.taskClientComments.some(c => c.id === comment.id);
      if (exists) {
        return state;
      }
      return {
        taskClientComments: [...state.taskClientComments, comment]
      };
    });
  },

  // Paginated tasks with server-side filtering
  fetchTasksPaginated: async (options) => {
    const { filters, cursor, limit = TASKS_PAGE_SIZE, reset = false } = options;

    // If reset, clear existing tasks and set loading
    if (reset) {
      set({
        tasks: [],
        tasksPagination: { cursor: null, hasMore: true, loading: true },
        currentTaskFilters: filters || null
      });
    } else {
      set(state => ({
        tasksPagination: { ...state.tasksPagination, loading: true }
      }));
    }

    try {
      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Build query with server-side filters
      let query = supabase
        .from('tasks')
        .select(`
          id,
          project_id,
          title,
          description,
          status,
          priority,
          assigned_to,
          assigned_client_access_id,
          assigned_collaborator_access_id,
          due_date,
          start_date,
          completed_at,
          created_at,
          updated_at,
          created_by,
          section,
          "order",
          is_header,
          is_client_request,
          sprint_added_at,
          review_at,
          dependencies,
          is_recurring,
          recurrence_type,
          recurrence_interval,
          recurrence_unit,
          recurrence_end_type,
          recurrence_end_date,
          recurrence_max_count,
          recurrence_count,
          recurrence_parent_id,
          recurrence_source_id,
          original_due_date,
          reminder_days_before,
          location,
          custom_fields,
          metadata,
          time_entries:task_time_entries (duration_minutes, ended_at),
          project:projects!inner (
            id,
            name,
            metadata,
            organization_id,
            deal_id,
            client:clients (
              id,
              business_name
            ),
            deal:deals (
              id,
              deal_services (
                service_id,
                service:services (
                  id,
                  title
                )
              )
            )
          ),
          assignee:user_profiles!tasks_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          created_by_user:user_profiles!tasks_created_by_fkey (
            id,
            email,
            full_name
          ),
          comments:task_comments (
            id,
            content,
            created_at,
            is_system_comment,
            author:user_profiles!task_comments_created_by_fkey (
              id,
              email,
              full_name
            ),
            attachments:task_comment_attachments (
              id,
              comment_id,
              name,
              url,
              type,
              size,
              created_by,
              created_at
            )
          ),
          attachments:task_attachments (
            id,
            name,
            url,
            type,
            size,
            created_at,
            created_by
          ),
          checklist:task_checklist!left (
            id,
            title,
            description,
            is_required,
            is_completed,
            completed_at,
            estimated_minutes,
            time_spent_minutes,
            sort_order,
            completed_by
          ),
          tags:task_tag_assignments!left (
            tag:project_task_tags!inner (
              id,
              name,
              color,
              sort_order,
              is_active
            )
          )
        `)
        .eq('project.organization_id', userProfile.organization_id)
        .eq('project.archived', false);

      // Server-side filters
      if (filters?.assignedTo) {
        // "Non assegnata" non e' l'id di una persona: chiede le task con la
        // casella vuota. Passandolo com'e' il database rifiutava la richiesta
        // (uuid non valido) e l'elenco restava vuoto.
        if (filters.assignedTo === '__unassigned__') {
          query = query.is('assigned_to', null);
        } else {
          query = query.eq('assigned_to', filters.assignedTo);
        }
      }

      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      } else if (!filters?.showCompleted) {
        // If no status filter and showCompleted is false, exclude completed
        query = query.neq('status', 'completed');
      }

      if (filters?.priority && filters.priority.length > 0) {
        query = query.in('priority', filters.priority);
      }

      if (filters?.searchQuery && filters.searchQuery.trim()) {
        const searchTerm = filters.searchQuery.trim();
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
      }

      // Exclude header tasks (section headers)
      query = query.eq('is_header', false);

      // Cursor-based pagination
      const effectiveCursor = reset ? null : (cursor || get().tasksPagination.cursor);
      if (effectiveCursor) {
        query = query.gt('created_at', effectiveCursor);
      }

      // Order and limit
      query = query
        .order('created_at', { ascending: true })
        .limit(limit);

      const { data: tasksData, error: tasksError } = await query;
      if (tasksError) throw tasksError;

      // Transform tasks to extract tags from assignments
      const transformedTasks = (tasksData || []).map((task: any) => ({
        ...task,
        tags: task.tags
          ?.map((assignment: any) => assignment.tag)
          .filter((tag: any) => tag && tag.is_active)
          .sort((a: any, b: any) => a.sort_order - b.sort_order) || [],
        // Somma delle sole voci chiuse: un timer in corso non deve comparire nel totale di riga
        total_time_minutes: (task.time_entries || [])
          .filter((e: any) => e.ended_at !== null)
          .reduce((sum: number, e: any) => sum + (e.duration_minutes ?? 0), 0)
      }));

      // Calculate new cursor and hasMore
      const lastTask = transformedTasks[transformedTasks.length - 1];
      const newCursor = lastTask?.created_at || effectiveCursor;
      const hasMore = transformedTasks.length === limit;

      // Update state
      set(state => ({
        tasks: reset ? transformedTasks : [...state.tasks, ...transformedTasks],
        tasksPagination: {
          cursor: newCursor,
          hasMore,
          loading: false
        }
      }));

    } catch (error) {
      console.error('Error fetching paginated tasks:', error);
      set(state => ({
        error: friendlyDbError(error),
        tasksPagination: { ...state.tasksPagination, loading: false }
      }));
    }
  },

  fetchMoreTasks: async () => {
    const { tasksPagination, currentTaskFilters } = get();

    // Don't fetch if already loading or no more tasks
    if (tasksPagination.loading || !tasksPagination.hasMore) {
      return;
    }

    await get().fetchTasksPaginated({
      filters: currentTaskFilters || undefined,
      cursor: tasksPagination.cursor,
      reset: false
    });
  }
}));